import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Input,
  InputAdornment,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import useSEO from "../seo/use-seo";
import StandardPageHelmet from "../seo/components/StandardPageHelmet";
import {
  ChevronLeft,
  ChevronRight,
  Check,
  RefreshCw,
} from "lucide-react";
import chooseUserTypeStyle from "./styles/chooseUserTypeStyle";
import complatedProfileStyle from "./styles/complateProfileStyle";
import {
  validateUserFirstName,
  validateUserLastName,
  validateBirthdayDate,
} from "../validators/profileFormValidators";
import ViewerLogo from "../assets/images/icons/viewerFlowImage.png";
import CreatorLogo from "../assets/images/icons/creatorFlowImage.png";
import Logo from "../assets/Logo.svg";
import IdentityVerificationIcon from "../assets/images/icons/70d011a15fd0a3b49eaaaf0c6ce30f98fa317e63.png";
import Step1Icon from "../assets/images/icons/ec8a547022d7bc2df94d81a7ccc6bc6cab485a3a.png";
import Step2Icon from "../assets/images/icons/8a3ae6bc017e09cebe7cd44af5dd91c340e58db9.png";
import Step3Icon from "../assets/images/icons/9047e3ae413d79ebc9cbfa6a8089d3646d03d565.png";
import SuccessfulVerificationIcon from "../assets/images/icons/f9d1e315c3bea4aa050b61ff98799f2116516fc8.png";
import UnsuccessfulVerificationIcon from "../assets/images/icons/e4d5d1416eedab24ec8763a525fb31332b5e2e99.png";
import PendingVerificationIcon from "../assets/images/icons/4136ac5e2a265e7b43b9329d8aedfd2417390380.png";
import ArrowBackRightIcon from "../assets/images/icons/arrowBackRight.png";
import {
  editProfileDataAPI,
  checkUsernameAPI,
} from "../api/userApi";
import { showToast } from "../utils/toast";
import { Redirect, useHistory } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../redux/typedHooks";
import { setUser, logoutUser } from "../redux/modules/userSlice";
import { clearTokens } from "../utils/tokens";
import { COUNTRIES } from "../config/countries";
import { CITIES } from "../config/cities";
import { autoDetectCountry, countryToFlag } from "../utils/countryDetection";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import {
  createDiditSessionAPI,
  deleteVerificationAPI,
  getDiditVerificationByUserIdAPI,
} from "../api/diditApi";
import { CalendarToday } from "@material-ui/icons";
import UploadAvatarContent from "./components/uploadAvatarContent";
import UploadMediaModal from "./components/UploadMediaModal";
import CameraIcon from "../assets/images/mynaui_camera-solid.svg";
import useMedia from "../hooks/useMedia";
import OnboardingLayout from "../layouts/onboarding.layout";
import {
  trackOnboardingStarted,
  trackOnboardingCompleted,
  trackProfileCompleted,
  trackVerificationCompleted,
  trackVerificationStarted,
  trackVerificationStepCompleted,
  trackVerificationStepStarted,
  trackDiditVerificationFailureOutcome,
  registerDiditVerificationAttempt,
  normalizeDiditStatus,
  isDiditFailureStatus,
  isDiditSuccessStatus,
  getDiditVerificationAttemptCount,
} from "../analytics/onboardingMixpanel";
import {
  identifyMixpanelUser,
  resetMixpanelIdentity,
  setMixpanelPeopleProfile,
} from "../config/mixpanel";
import {
  getCreatorOnboardingFinalizeFields,
  isStagingCreatorOnboardingBypassEnabled,
  STAGING_MOCK_DIDIT_VERIFICATION_DATA,
  STAGING_MOCK_VERIFICATION_STATUS,
} from "../config/stagingCreatorOnboarding";

const stagingCreatorBypass = isStagingCreatorOnboardingBypassEnabled();

const ChooseUserType = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [step, setStep] = useState(() => {
    // Get saved step from localStorage, default to 1
    const savedStep = localStorage.getItem("onboarding-step");
    return savedStep ? parseInt(savedStep, 10) : 1;
  });
  const history = useHistory();
  const classes = chooseUserTypeStyle();
  const seo = useSEO("choose-user-type");

  const user = useAppSelector<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    username: string;
    DOB: string;
    bio: string;
    isAdmin: boolean;
    profileImg: string;
    userType: {
      _id: string;
      title: string;
    };
  } | null>(state => state.user.currentUser);
  const dispatch = useAppDispatch();
  const onboardingFlowEnteredTracked = useRef(false);
  const profileCompletedTracked = useRef(false);
  const verificationStartedTracked = useRef(false);
  const verificationCompletedTracked = useRef(false);
  const lastPolledVerificationStatus = useRef<string | null>(null);

  const [selectedType, setSelectedType] = useState<string>(() => {
    // Get saved type from localStorage
    const savedType = localStorage.getItem("onboarding-selected-type");
    if (savedType) {
      return savedType;
    }
    // Default to "model" - will be updated by useEffect if user has different type
    return "model";
  });
  const creatorStyleProps = { selected: selectedType === "model" };
  const viewerStyleProps = { selected: selectedType === "viewer" };
  const creatorClasses = chooseUserTypeStyle(creatorStyleProps);
  const viewerClasses = chooseUserTypeStyle(viewerStyleProps);
  const profileStyle = complatedProfileStyle();
  const viewerIdType = `6800a68085ec077c7c266c27`;
  const modelIdType = `6800a63485ec077c7c266c25`;

  const [profileData, setProfileData] = useState<{
    firstName: string;
    lastName: string;
    email: string;
    username: string;
    DOB: string;
    usertype: string;
    publicImageUsageConsent: boolean;
    gender: string;
    bio: string;
    location: string;
    sexualOrientation: string;
    ethnicity: string;
    annualIncome: string;
    country?: string;
    city?: string;
    subdivision?: string;
    profileImg: File | null;
  }>({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    email: user?.email || "",
    username:
      user?.username && user.username.startsWith("user_")
        ? ""
        : user?.username || "",
    DOB: user?.DOB || "", // Default to empty string instead of user?.DOB
    usertype: user?.userType?._id || modelIdType,
    publicImageUsageConsent: false,
    gender: "",
    bio: user?.bio || "",
    location: "",
    sexualOrientation: "",
    ethnicity: "",
    annualIncome: "",
    country: "",
    city: "",
    subdivision: "",
    profileImg: null,
  });

  const { imageUrl: profileImgUrl } = useMedia(user?.profileImg);

  const [, setProfileCompleted] = useState(false);
  const [, setProfileViewerCompleted] = useState(false);
  const [diditVerificationData, setDiditVerificationData] = useState<any>(null);
  const [verificationStatus, setVerificationStatus] =
    useState<string>("pending");
  const [popupBlocked, setPopupBlocked] = useState<boolean>(false);
  const [sessionUrl, setSessionUrl] = useState<string | undefined>(() => {
    // Get saved session URL from sessionStorage
    return sessionStorage.getItem("didit-session-url") || undefined;
  });
  // iframe visibility is derived from sessionUrl

  // Error states for viewer form
  const [viewerErrors, setViewerErrors] = useState<{
    firstName?: string;
    lastName?: string;
    username?: string;
    DOB?: string;
    profileImg?: string;
    bio?: string;
  }>({});

  // Username availability check state
  const [isCheckingUsername, setIsCheckingUsername] = useState<boolean>(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean | null>(
    null
  );

  const [creatorErrors, setCreatorErrors] = useState<{
    firstName?: string;
    lastName?: string;
    username?: string;
    DOB?: string;
    profileImg?: string;
    bio?: string;
  }>({});
  const [isGoToPayment, setIsGoToPayment] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState<boolean>(false);
  // Agreement checkboxes state
  const [agreementAccepted, setAgreementAccepted] = useState<boolean>(false);
  const [imageUsageAgreed, setImageUsageAgreed] = useState<boolean>(false);
  // Upload media modal state
  const [uploadMediaModalOpen, setUploadMediaModalOpen] =
    useState<boolean>(false);

  const isAdult = () => {
    if (!profileData.DOB || profileData.DOB.trim() === "") return false;
    const birthDate = new Date(profileData.DOB);
    if (Number.isNaN(birthDate.getTime())) return false;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age >= 18;
  };
  const validateAllFields = () => {
    const isFirstNameValid =
      validateUserFirstName(profileData.firstName) === true;
    const isLastNameValid = validateUserLastName(profileData.lastName) === true;
    const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email);
    const isDOBValid = validateBirthdayDate(profileData.DOB) === true;
    // const isBioValid = validateUserBio(profileData.bio) === true;
    // const isGenderValid = validateUserGender(profileData.gender) === true;
    // const isLocationValid = validateUserLocation(profileData.location) === true;
    // const isCityValid = profileData.city && profileData.city.length >= 2;
    // const isSexualOrientationValid =
    //   validateUserSexualOrientation(profileData.sexualOrientation) === true;
    // const isEthnicityValid =
    //   validateUserEthnicity(profileData.ethnicity) === true;

    return (
      isFirstNameValid && isLastNameValid && isEmailValid && isDOBValid
      // isGenderValid &&
      // isBioValid &&
      // isLocationValid &&
      // isCityValid &&
      // isSexualOrientationValid &&
      // isEthnicityValid
    );
  };
  // Validation functions for creator form
  const validateCreatorField = (
    field: keyof typeof creatorErrors,
    value: any
  ) => {
    let error = "";

    switch (field) {
      case "firstName":
        if (!value || value.trim() === "") {
          error = "First name is required";
        } else if (value.length < 2) {
          error = "First name must be at least 2 characters";
        }
        break;
      case "lastName":
        if (!value || value.trim() === "") {
          error = "Last name is required";
        } else if (value.length < 2) {
          error = "Last name must be at least 2 characters";
        }
        break;
      case "username":
        if (value && value.trim() !== "") {
          const usernameRegex = /^[a-zA-Z0-9_-]+$/;
          if (value.length < 3) {
            error = "Username must be at least 3 characters";
          } else if (value.length > 16) {
            error = "Username must be less than 16 characters";
          } else if (!usernameRegex.test(value)) {
            error =
              "Username can only contain letters, numbers, underscores, and hyphens";
          }
        }
        break;
      case "DOB":
        if (!value || value.trim() === "") {
          error = "Date of birth is required";
        } else {
          const birthDate = new Date(value);
          const today = new Date();
          const age = today.getFullYear() - birthDate.getFullYear();
          if (age < 18) {
            error = "You must be at least 18 years old";
          }
        }
        break;
      case "profileImg":
        if (!value) {
          error = "Profile image is required";
        }
        break;
      case "bio":
        // Bio is optional, no validation needed
        break;
    }

    return error;
  };

  const handleCreatorFieldChange = (
    field: keyof typeof creatorErrors,
    value: any
  ) => {
    // Update profile data
    setProfileData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (creatorErrors[field]) {
      setCreatorErrors(prev => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleCreatorFieldBlur = (
    field: keyof typeof creatorErrors,
    value: any
  ) => {
    const error = validateCreatorField(field, value);
    setCreatorErrors(prev => ({
      ...prev,
      [field]: error,
    }));
  };

  const isCreatorFormValid = () => {
    const isDOBAdult = isAdult();
    return (
      profileData.firstName.trim() !== "" &&
      profileData.lastName.trim() !== "" &&
      profileData.DOB.trim() !== "" &&
      isDOBAdult &&
      (profileData.profileImg !== null || profileImgUrl !== null) &&
      usernameAvailable !== false
    );
  };

  // Validation functions for viewer form
  const validateViewerField = (
    field: keyof typeof viewerErrors,
    value: any
  ) => {
    let error = "";

    switch (field) {
      case "firstName":
        if (!value || value.trim() === "") {
          error = "First name is required";
        } else if (value.length < 2) {
          error = "First name must be at least 2 characters";
        }
        break;
      case "lastName":
        if (!value || value.trim() === "") {
          error = "Last name is required";
        } else if (value.length < 2) {
          error = "Last name must be at least 2 characters";
        }
        break;
      case "username":
        if (!value || value.trim() === "") {
          error = "Username is required";
        } else if (value.length < 3) {
          error = "Username must be at least 3 characters";
        }
        break;
      case "DOB":
        if (!value || value.trim() === "") {
          error = "Date of birth is required";
        } else {
          const birthDate = new Date(value);
          const today = new Date();
          const age = today.getFullYear() - birthDate.getFullYear();
          if (age < 18) {
            error = "You must be at least 18 years old";
          }
        }
        break;
      case "profileImg":
        if (!value) {
          error = "Profile image is required";
        }
        break;
      case "bio":
        // Bio is optional, no validation needed
        break;
    }

    return error;
  };

  const handleViewerFieldChange = (
    field: keyof typeof viewerErrors,
    value: any
  ) => {
    // Update profile data
    setProfileData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing, except for username "already taken" error
    if (viewerErrors[field]) {
      // Don't clear "already taken" error automatically - let the API check handle it
      if (
        field === "username" &&
        viewerErrors[field] === "This username is already taken"
      ) {
        // Keep the error, but reset availability to trigger new check
        setUsernameAvailable(null);
      } else {
        setViewerErrors(prev => ({
          ...prev,
          [field]: "",
        }));
      }
    }

    // Reset username availability when username changes
    if (field === "username") {
      setUsernameAvailable(null);
    }
  };

  const handleViewerFieldBlur = (
    field: keyof typeof viewerErrors,
    value: any
  ) => {
    const error = validateViewerField(field, value);
    setViewerErrors(prev => ({
      ...prev,
      [field]: error,
    }));
  };

  // Username availability check with debounce
  useEffect(() => {
    const username = profileData.username?.trim();

    // Don't check if username is empty or too short
    if (!username || username.length < 3) {
      setUsernameAvailable(null);
      setIsCheckingUsername(false);
      return;
    }

    // Don't check if username format is invalid
    const usernameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!usernameRegex.test(username)) {
      setUsernameAvailable(null);
      setIsCheckingUsername(false);
      return;
    }

    // Set checking state
    setIsCheckingUsername(true);

    // Debounce: wait 430ms before checking
    const timeoutId = setTimeout(async () => {
      try {
        const response = await checkUsernameAPI(username);
        if (response.success) {
          // If username exists, it's not available
          const isAvailable = !response.data.exists;
          setUsernameAvailable(isAvailable);

          if (response.data.exists) {
            // Always show error if username is taken
            setViewerErrors(prev => ({
              ...prev,
              username: "This username is already taken",
            }));
          } else {
            // Clear "already taken" error only if username is available
            setViewerErrors(prev => {
              if (prev.username === "This username is already taken") {
                return {
                  ...prev,
                  username: "",
                };
              }
              return prev;
            });
          }
        }
      } catch (error: any) {
        console.error("Error checking username:", error);
        // On error, check if it's because username is taken
        if (
          error?.data?.exists === true ||
          error?.message?.toLowerCase().includes("taken")
        ) {
          setUsernameAvailable(false);
          setViewerErrors(prev => ({
            ...prev,
            username: "This username is already taken",
          }));
        } else {
          // On other errors, don't set availability (keep null)
          setUsernameAvailable(null);
        }
      } finally {
        setIsCheckingUsername(false);
      }
    }, 430);

    // Cleanup timeout on unmount or when username changes
    return () => {
      clearTimeout(timeoutId);
    };
  }, [profileData.username]);

  const isViewerFormValid = () => {
    const isDOBAdult = isAdult();
    return (
      profileData.firstName.trim() !== "" &&
      profileData.lastName.trim() !== "" &&
      profileData.email.trim() !== "" &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email) &&
      profileData.DOB.trim() !== "" &&
      isDOBAdult &&
      (profileData.profileImg !== null || profileImgUrl !== null) &&
      profileData.username.trim() !== "" &&
      usernameAvailable !== false // Username must be available
    );
  };

  const validateAllFieldsViewer = () => {
    const isFirstNameValid =
      validateUserFirstName(profileData.firstName) === true;
    const isLastNameValid = validateUserLastName(profileData.lastName) === true;
    // const isEmailValid = validateUserEmail(profileData.email) === true;
    const isDOBValid = validateBirthdayDate(profileData.DOB) === true;
    // const isBioValid = validateUserBio(profileData.bio) === true;
    // const isGenderValid = validateUserGender(profileData.gender) === true;
    // const isLocationValid = validateUserLocation(profileData.location) === true;
    // const isCityValid = profileData.city && profileData.city.length >= 2;
    // const isSexualOrientationValid =
    //   validateUserSexualOrientation(profileData.sexualOrientation) === true;
    // const isEthnicityValid =
    //   validateUserEthnicity(profileData.ethnicity) === true;
    // const isAnnualIncomeValid =
    //   validateUserAnnualIncome(profileData.annualIncome) === true;
    return (
      isFirstNameValid &&
      isLastNameValid &&
      // isEmailValid &&
      isDOBValid
      // isGenderValid &&
      // isBioValid &&
      // isLocationValid &&
      // isCityValid &&
      // isSexualOrientationValid &&
      // isEthnicityValid &&
      // isAnnualIncomeValid
    );
  };

  // Function to save current step to localStorage
  const saveCurrentStep = useCallback((stepNumber: number) => {
    localStorage.setItem("onboarding-step", stepNumber.toString());
  }, []);

  // Function to save selected type to localStorage
  const saveSelectedType = useCallback((type: string) => {
    localStorage.setItem("onboarding-selected-type", type);
  }, []);

  // Function to save session URL to sessionStorage
  const saveSessionUrl = useCallback((url: string) => {
    sessionStorage.setItem("didit-session-url", url);
  }, []);

  // Function to clear session URL from sessionStorage
  const clearSessionUrl = useCallback(() => {
    sessionStorage.removeItem("didit-session-url");
  }, []);

  // Function to clear saved step (when onboarding is complete)
  const clearSavedStep = useCallback(() => {
    localStorage.removeItem("onboarding-step");
    localStorage.removeItem("onboarding-selected-type");
    clearSessionUrl(); // Clear session URL as well
  }, [clearSessionUrl]);

  // Initialize selectedType from user only if not already saved in localStorage
  useEffect(() => {
    if (user) {
      const savedType = localStorage.getItem("onboarding-selected-type");
      if (!savedType) {
        // Only set from user if no saved type exists
        const userType = user?.userType?.title || "model";
        setSelectedType(userType);
        saveSelectedType(userType);
      }
    }
  }, [user, saveSelectedType]);

  // Clear default "user_XXXXX" username on onboarding
  useEffect(() => {
    if (user?.username && user.username.startsWith("user_")) {
      setProfileData(prev => ({
        ...prev,
        username: "",
      }));
    }
  }, [user?.username]);

  // Save current step whenever it changes
  useEffect(() => {
    saveCurrentStep(step);
  }, [step, saveCurrentStep]);

  useEffect(() => {
    if (user?.id) {
      identifyMixpanelUser(user.id);
      setMixpanelPeopleProfile({
        email: user?.email,
        name: user?.username || `${user?.firstName || ""} ${user?.lastName || ""}`.trim(),
      });
    }
  }, [user?.id, user?.email, user?.username, user?.firstName, user?.lastName]);

  useEffect(() => {
    if (onboardingFlowEnteredTracked.current) {
      return;
    }
    onboardingFlowEnteredTracked.current = true;
    trackOnboardingStarted({
      account_type: selectedType,
      user_id: user?.id,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- один раз при вході на onboarding
  }, []);

  useEffect(() => {
    if (step >= 2 && !profileCompletedTracked.current) {
      profileCompletedTracked.current = true;
      trackProfileCompleted({
        account_type: selectedType,
        user_id: user?.id,
      });
    }
  }, [step, selectedType, user?.id]);

  // Save selected type whenever it changes
  useEffect(() => {
    saveSelectedType(selectedType);
  }, [selectedType, saveSelectedType]);

  // Save session URL whenever it changes
  useEffect(() => {
    if (sessionUrl) {
      saveSessionUrl(sessionUrl);
    }
  }, [sessionUrl, saveSessionUrl]);

  // sessionUrl changes are handled by UI
  useEffect(() => {}, [sessionUrl]);

  useEffect(() => {
    setProfileCompleted(!!validateAllFields());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    profileData.firstName,
    profileData.lastName,
    profileData.DOB,
    profileData.gender,
    profileData.bio,
    profileData.location,
    profileData.city,
    profileData.sexualOrientation,
    profileData.ethnicity,
    profileData.profileImg,
    agreementAccepted,
  ]);

  useEffect(() => {
    setProfileViewerCompleted(!!validateAllFieldsViewer());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    profileData.firstName,
    profileData.lastName,
    profileData.DOB,
    profileData.gender,
    profileData.bio,
    profileData.location,
    profileData.city,
    profileData.sexualOrientation,
    profileData.ethnicity,
    profileData.annualIncome,
    profileData.profileImg,
  ]);

  const handleProfileChange = () => {
    // const validationResult = validateAllFields({
    //     ...profileData,
    //     publicImageUsageConsent:
    //         selectedType === "model" ? agreementAccepted : true,
    // });
    // if (validationResult !== true) {
    //     showToast(validationResult, "error");
    //     return;
    // }
    const isValid = validateAllFields();
    if (!isValid) {
      showToast("Please complete all required fields", "error");
      return;
    }
    const formData = new FormData();
    formData.append("firstName", profileData.firstName);
    formData.append("lastName", profileData.lastName);
    formData.append("email", profileData.email);
    // formData.append("email", profileData.email);
    formData.append("DOB", profileData.DOB);
    formData.append("usertype", profileData.usertype);
    // formData.append("bio", profileData.bio);
    // formData.append("gender", profileData.gender);
    // formData.append("location", profileData.location);
    // formData.append("sexualOrientation", profileData.sexualOrientation);
    // formData.append("ethnicity", profileData.ethnicity);
    // formData.append("annualIncome", profileData.annualIncome);
    // formData.append(
    //   "publicImageUsageConsent",
    //   (selectedType === "model" ? agreementAccepted : true).toString()
    // );
    if (profileData.country) {
      formData.append("country", profileData.country);
    }
    if (profileData.city) {
      formData.append("city", profileData.city);
    }
    if (profileData.subdivision) {
      formData.append("subdivision", profileData.subdivision);
    }
    // Build location string from city, subdivision, and country
    if (profileData.city || profileData.country) {
      const locationParts = [];
      if (profileData.city) locationParts.push(profileData.city);
      if (profileData.subdivision) locationParts.push(profileData.subdivision);
      if (profileData.country) locationParts.push(profileData.country);
      formData.append("location", locationParts.join(", "));
    }
    if (profileData.profileImg instanceof File) {
      formData.append("image", profileData.profileImg);
    }
    editProfileDataAPI(formData)
      .then(res => {
        if (res?.success) {
          // Update Redux store with fresh user data
          if (res?.user && user) {
            dispatch(
              setUser({
                ...user,
                ...res?.user,
              })
            );
          }

          showToast("Profile updated successfully!");
          if (selectedType === "model") {
            return nextStep();
            // createDiditSessionAPI(
            //   user?.id,
            //   `${window.location.origin}/verification-success`
            // )
            //   .then(() => {
            //     nextStep(); // Go to Didit verification status step
            //   })
            //   .catch(err => {
            //     showToast("Error starting verification process", "error");
            //     console.error("Error starting Didit verification:", err);
            //   });
          } else {
            // Viewer: complete onboarding (no follow step)
            editProfileDataAPI({
              isOnboarding: true,
              isOnboaring: true,
              validated: true,
            })
              .then((finishRes: any) => {
                if (finishRes?.user && user) {
                  dispatch(
                    setUser({
                      ...user,
                      ...finishRes.user,
                    })
                  );
                }
                showToast("Onboarding completed!", "success");
                clearSavedStep();
                trackOnboardingCompleted({
                  account_type: "viewer",
                  user_id: user?.id,
                });
                history.push("/");
              })
              .catch((e: any) => {
                showToast(
                  e?.message || "Failed to complete onboarding",
                  "error"
                );
              });
          }
        } else {
          showToast("Update profile error!", "error");
        }
      })
      .catch(err => {
        showToast(err?.message || "Server error!", "error");
      });
  };

  const handleTypeChange = async (type: string, isNext = true) => {
    const updatedProfileData = {
      ...profileData,
      usertype: type === "model" ? modelIdType : viewerIdType,
    };

    setProfileData(updatedProfileData);
    setSelectedType(type);
    saveSelectedType(type); // Save selected type to localStorage
    editProfileDataAPI({ usertype: updatedProfileData.usertype })
      .then(res => {
        // Update Redux store with fresh user data
        if (res?.user && user) {
          dispatch(
            setUser({
              ...user,
              ...res?.user,
              userType: {
                _id: type === "model" ? modelIdType : viewerIdType,
                title: type,
                description: type === "model" ? "model" : "viewer",
              },
            })
          );
        }

        showToast(`Profile type changed to ${type}`);
        isNext && nextStep();
      })
      .catch(err => {
        showToast(`Error while changing type to ${type}`, "error");
        console.error("API error:", err);
      });
  };

  /* const startDiscover = () => {
    // For viewers, complete profile and redirect to home
    editProfileDataAPI({
      validated: true,
      isOnboarding: true,
    })
      .then(res => {
        if (res?.success) {
          // Update Redux store with fresh user data
          if (res?.user && user) {
            dispatch(
              setUser({
                ...user,
                ...res?.user,
              })
            );
          }

          clearSavedStep(); // Clear saved step when onboarding is complete
          showToast("Profile set up successfully!");
          history.push("/");
        } else {
          showToast("Error setting up profile data!", "error");
        }
      })
      .catch(err => {
        showToast(err?.message || "Server error!", "error");
        console.log("Update profile error: ", err);
      });
  }; */

  /* const finishOnboarding = () => {
    // Complete the onboarding process
    editProfileDataAPI({
      validated: true,
    })
      .then(res => {
        if (res?.success) {
          // Update Redux store with fresh user data
          if (res?.user && user) {
            dispatch(
              setUser({
                ...user,
                ...res?.user,
              })
            );
          }

          clearSavedStep(); // Clear saved step when onboarding is complete
          showToast("Onboarding completed successfully!");
          history.push("/");
        } else {
          showToast("Error completing onboarding!", "error");
        }
      })
      .catch(err => {
        showToast(err?.message || "Server error!", "error");
        console.log("Update profile error: ", err);
      });
  }; */

  //   DiditVerification
  const startDiditVerification = async () => {
    if (stagingCreatorBypass) {
      setDiditVerificationData(STAGING_MOCK_DIDIT_VERIFICATION_DATA);
      setVerificationStatus(STAGING_MOCK_VERIFICATION_STATUS);
      sessionStorage.setItem(
        "diditVerificationStatus",
        STAGING_MOCK_VERIFICATION_STATUS
      );
      clearSessionUrl();
      showToast("Staging: identity verification skipped.", "info");
      return;
    }

    if (!verificationStartedTracked.current) {
      verificationStartedTracked.current = true;
      trackVerificationStarted({ user_id: user?.id });
      trackVerificationStepStarted({
        user_id: user?.id,
        step_type: "id_check",
      });
    }

    try {
      if (
        diditVerificationData?.didit_verifications?.[0]?.status === "Approved"
      ) {
        showToast("Your verification is already approved!", "success");
      } else if (
        diditVerificationData?.didit_verifications?.[0]?.status ===
        "In Progress"
      ) {
        showToast("Your verification is already in progress.", "info");
      } else if (
        diditVerificationData?.didit_verifications?.[0]?.status === "In Review"
      ) {
        showToast(
          "Your verification is under review. Please wait for the result.",
          "info"
        );
      } else if (
        diditVerificationData?.didit_verifications?.[0]?.status ===
          "Declined" ||
        diditVerificationData?.didit_verifications?.[0]?.status ===
          "KYC Expired" ||
        diditVerificationData?.didit_verifications?.[0]?.status === "Expired" ||
        diditVerificationData?.didit_verifications?.[0]?.status ===
          "Abandoned" ||
        diditVerificationData?.didit_verifications?.[0]?.status === "unknown" ||
        diditVerificationData?.didit_verifications?.[0]?.status === "pending" ||
        diditVerificationData?.didit_verifications?.[0]?.status ===
          "Not Started"
      ) {
        // For these statuses, remove previous session and create new one
        showToast(
          `Your previous verification was ${diditVerificationData?.didit_verifications?.[0]?.status}. Starting a new verification session.`,
          "warning"
        );
        if (diditVerificationData?.current_didit_verification_id) {
          try {
            await deleteVerificationAPI(
              diditVerificationData?.current_didit_verification_id
            );
          } catch (error) {
            console.error(
              "Error deleting previous verification session:",
              error
            );
            showToast(
              "Warning: Could not delete previous session, but continuing with new verification",
              "warning"
            );
          }
        }
      }

      setPopupBlocked(false); // Reset popup blocked state
      setSessionUrl(undefined); // Reset iframe state
      clearSessionUrl(); // Clear any existing session URL

      const result = await createDiditSessionAPI(
        user?.id,
        `${window.location.origin}/verification-success`
      );

      // Store the session data and show iframe
      if (result?.session_url) {
        verificationCompletedTracked.current = false;
        registerDiditVerificationAttempt({
          user_id: user?.id,
          source: "user_click",
        });
        setSessionUrl(result.session_url);
        saveSessionUrl(result.session_url); // Save to sessionStorage
        // iframe visible due to sessionUrl

        showToast(
          "Verification process started. Please complete the verification below."
        );
      }
    } catch (error) {
      console.error("Error starting Didit verification:", error);

      // Handle specific error cases
      if ((error as any)?.type === "ACTIVE_VERIFICATION_EXISTS") {
        // User already has an active verification
        const existingStatus = (error as any).status;
        setVerificationStatus(existingStatus);

        if (existingStatus === "approved") {
          showToast("Your verification is already approved!", "success");
        } else if (existingStatus === "in_progress") {
          showToast("Your verification is already in progress.", "info");
        } else if (existingStatus === "in_review") {
          showToast(
            "Your verification is under review. Please wait for the result.",
            "info"
          );
        } else if (
          existingStatus === "declined" ||
          existingStatus === "kyc_expired" ||
          existingStatus === "expired" ||
          existingStatus === "abandoned" ||
          existingStatus === "unknown" ||
          existingStatus === "pending"
        ) {
          // For these statuses, remove previous session and create new one
          showToast(
            `Your previous verification was ${existingStatus}. Starting a new verification session.`,
            "warning"
          );
          await startNewVerificationSession();
        } else {
          showToast(`Verification status: ${existingStatus}`, "info");
        }
      } else {
        trackDiditVerificationFailureOutcome({
          user_id: user?.id,
          status: "session_create_error",
          failure_source: "session_create",
          failure_reason:
            (error as any)?.message ||
            (error instanceof Error ? error.message : "session_create_failed"),
          attempt_number: getDiditVerificationAttemptCount(),
        });
        showToast("Error starting verification process", "error");
      }
    }
  };

  const startNewVerificationSession = async () => {
    try {
      // Clear any existing session data first
      clearSessionUrl();
      setSessionUrl(undefined);
      // no-op

      // Try to create a new session (backend should handle removing previous session)
      const result = await createDiditSessionAPI(
        user?.id,
        `${window.location.origin}/verification-success`
      );

      if (result?.session_url) {
        verificationCompletedTracked.current = false;
        registerDiditVerificationAttempt({
          user_id: user?.id,
          source: "retry_after_failure",
        });
        setSessionUrl(result.session_url);
        saveSessionUrl(result.session_url);
        // no-op
        setVerificationStatus("in_progress");
        showToast("New verification session started successfully!");
      }
    } catch (error) {
      console.error("Error starting new verification session:", error);

      // Handle the case where there's still an active session
      if ((error as any)?.type === "ACTIVE_VERIFICATION_EXISTS") {
        const existingStatus = (error as any).status;

        if (
          existingStatus === "declined" ||
          existingStatus === "kyc_expired" ||
          existingStatus === "expired" ||
          existingStatus === "abandoned" ||
          existingStatus === "unknown" ||
          existingStatus === "pending"
        ) {
          showToast(
            `Unable to start new session. Previous ${existingStatus} session still exists. Please contact support.`,
            "error"
          );
        } else {
          showToast(`Previous verification status: ${existingStatus}`, "info");
        }
      } else {
        trackDiditVerificationFailureOutcome({
          user_id: user?.id,
          status: "session_create_error",
          failure_source: "api_error",
          failure_reason:
            (error as any)?.message ||
            (error instanceof Error ? error.message : "retry_session_failed"),
          attempt_number: getDiditVerificationAttemptCount(),
        });
        showToast("Error starting new verification session", "error");
      }
    }
  };

  const getStatusMessage = (status: string) => {
    const statusMessages = {
      "Not Started": {
        title: "",
        message: "",
        color: "#f06ec4",
        canRestart: true,
      },
      "In Progress": {
        title: "⏳ Verification in Progress",
        message:
          "Great! You've started the verification process. Please complete the steps below to finish your identity verification. This usually takes just a few minutes.",
        color: "#F59E0B",
        canRestart: false,
      },
      Approved: {
        title: "🎉 Verification Complete!",
        message:
          "Excellent! Your identity has been successfully verified. You're all set to start your creator journey. Click 'Next' to continue to the final step.",
        color: "#10B981",
        canRestart: false,
      },
      Declined: {
        title: "❌ Verification Needs Attention",
        message:
          "We weren't able to verify your identity with the information provided. This could be due to unclear documents or missing information. Don't worry - you can try again with better quality documents.",
        color: "#EF4444",
        canRestart: true,
      },
      "KYC Expired": {
        title: "⏰ Verification Expired",
        message:
          "Your previous verification has expired and needs to be renewed. This is a standard security measure. You can start a fresh verification process right now.",
        color: "#F59E0B",
        canRestart: true,
      },
      "In Review": {
        title: "🔍 Under Review",
        message:
          "Your verification is being reviewed by our compliance team. This is normal for some cases and usually takes 24-48 hours. We'll notify you via email once the review is complete.",
        color: "#3B82F6",
        canRestart: false,
      },
      Expired: {
        title: "⏰ Session Expired",
        message:
          "Your verification session timed out. This happens when the process takes too long. No worries - you can start a new verification session right away.",
        color: "#F59E0B",
        canRestart: true,
      },
      Abandoned: {
        title: "🚫 Verification Incomplete",
        message:
          "It looks like the verification process wasn't completed. This can happen if you close the browser or navigate away. You can start fresh whenever you're ready.",
        color: "#6B7280",
        canRestart: true,
      },
      unknown: {
        title: "❓ Status Unclear",
        message:
          "We're having trouble determining your verification status. This is rare, but easily fixed. You can start a new verification process to get back on track.",
        color: "#6B7280",
        canRestart: true,
      },
    };

    return (
      statusMessages[status as keyof typeof statusMessages] || {
        title: "❓ Unknown Status",
        message: `Verification status: ${status}. Please contact support if you need assistance.`,
        color: "#6B7280",
        canRestart: true,
      }
    );
  };

  /* const redirectToProfile = () => {
    // Redirect to profile with pending approval status
    history.push(`/${user?.username || user?.id}`);
  }; */

  const handleManualPopupOpen = () => {
    // Try to show the verification iframe manually
    if (sessionUrl) {
      // no-op
      setPopupBlocked(false);
      showToast("Verification iframe opened successfully!");
    } else if (diditVerificationData?.session_url) {
      setSessionUrl(diditVerificationData.session_url);
      saveSessionUrl(diditVerificationData.session_url);
      // no-op
      setPopupBlocked(false);
      showToast("Verification iframe opened successfully!");
    } else {
      startDiditVerification();
    }
  };

  const nextStep = useCallback(
    (currentStep?: number) => {
      const newStep = currentStep || step + 1;
      setStep(newStep);
      saveCurrentStep(newStep);
    },
    [step, saveCurrentStep]
  );
  const prevStep = useCallback(
    (currentStep?: number) => {
      const newStep = currentStep || step - 1;
      setStep(newStep);
      saveCurrentStep(newStep);
    },
    [step, saveCurrentStep]
  );
  const goToStep = useCallback(
    (targetStep: number) => {
      setStep(targetStep);
      saveCurrentStep(targetStep);
    },
    [saveCurrentStep]
  );
  // const fileInputRef = useRef<HTMLInputElement | null>(null);
  // const buildLocationString = (city?: string, country?: string) =>
  //   city && country ? `${city}, ${country}` : city || country || "";

  const parseLocationString = (loc?: string) => {
    if (!loc) return { city: "", country: "" };
    const [cityPart, ...rest] = loc.split(",").map(s => s.trim());
    return { city: cityPart || "", country: rest.join(", ").trim() || "" };
  };

  // Auto-detect country on mount
  useEffect(() => {
    const userCountry = (user as any)?.country;
    if (!profileData.country && !userCountry) {
      autoDetectCountry().then(detectedCountry => {
        if (detectedCountry) {
          setProfileData(prev => ({
            ...prev,
            country: detectedCountry,
          }));
        }
      });
    } else if (userCountry && !profileData.country) {
      setProfileData(prev => ({
        ...prev,
        country: userCountry,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (profileData && !profileData.country && profileData.location) {
      const { city, country } = parseLocationString(profileData.location);
      if (city || country) {
        setProfileData(p => ({
          ...p,
          city: p.city || city,
          country: p.country || country,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData.location]);

  // Subdivisions for countries with ISO 3166-2 support
  const SUBDIVISIONS: Record<string, string[]> = {
    US: [
      "Alabama",
      "Alaska",
      "Arizona",
      "Arkansas",
      "California",
      "Colorado",
      "Connecticut",
      "Delaware",
      "Florida",
      "Georgia",
      "Hawaii",
      "Idaho",
      "Illinois",
      "Indiana",
      "Iowa",
      "Kansas",
      "Kentucky",
      "Louisiana",
      "Maine",
      "Maryland",
      "Massachusetts",
      "Michigan",
      "Minnesota",
      "Mississippi",
      "Missouri",
      "Montana",
      "Nebraska",
      "Nevada",
      "New Hampshire",
      "New Jersey",
      "New Mexico",
      "New York",
      "North Carolina",
      "North Dakota",
      "Ohio",
      "Oklahoma",
      "Oregon",
      "Pennsylvania",
      "Rhode Island",
      "South Carolina",
      "South Dakota",
      "Tennessee",
      "Texas",
      "Utah",
      "Vermont",
      "Virginia",
      "Washington",
      "West Virginia",
      "Wisconsin",
      "Wyoming",
    ],
    CA: [
      "Alberta",
      "British Columbia",
      "Manitoba",
      "New Brunswick",
      "Newfoundland and Labrador",
      "Nova Scotia",
      "Ontario",
      "Prince Edward Island",
      "Quebec",
      "Saskatchewan",
      "Northwest Territories",
      "Nunavut",
      "Yukon",
    ],
  };

  // Get ISO code from country name
  const getCountryCode = (countryName: string): string | null => {
    const countryToCode: Record<string, string> = {
      "United States": "US",
      "United Kingdom": "GB",
      Canada: "CA",
      Australia: "AU",
      Afghanistan: "AF",
      Albania: "AL",
      Algeria: "DZ",
      Andorra: "AD",
      Angola: "AO",
      Argentina: "AR",
      Armenia: "AM",
      Austria: "AT",
      Azerbaijan: "AZ",
      Bahamas: "BS",
      Bahrain: "BH",
      Bangladesh: "BD",
      Barbados: "BB",
      Belarus: "BY",
      Belgium: "BE",
      Belize: "BZ",
      Benin: "BJ",
      Bhutan: "BT",
      Bolivia: "BO",
      "Bosnia and Herzegovina": "BA",
      Botswana: "BW",
      Brazil: "BR",
      Brunei: "BN",
      Bulgaria: "BG",
      "Burkina Faso": "BF",
      Burundi: "BI",
      "Cabo Verde": "CV",
      Cambodia: "KH",
      Cameroon: "CM",
      "Central African Republic": "CF",
      Chad: "TD",
      Chile: "CL",
      China: "CN",
      Colombia: "CO",
      Comoros: "KM",
      "Congo (Congo-Brazzaville)": "CG",
      "Costa Rica": "CR",
      Croatia: "HR",
      Cuba: "CU",
      Cyprus: "CY",
      "Czechia (Czech Republic)": "CZ",
      "Democratic Republic of the Congo": "CD",
      Denmark: "DK",
      Djibouti: "DJ",
      Dominica: "DM",
      "Dominican Republic": "DO",
      Ecuador: "EC",
      Egypt: "EG",
      "El Salvador": "SV",
      "Equatorial Guinea": "GQ",
      Eritrea: "ER",
      Estonia: "EE",
      Eswatini: "SZ",
      Ethiopia: "ET",
      Fiji: "FJ",
      Finland: "FI",
      France: "FR",
      Gabon: "GA",
      Gambia: "GM",
      Georgia: "GE",
      Germany: "DE",
      Ghana: "GH",
      Greece: "GR",
      Grenada: "GD",
      Guatemala: "GT",
      Guinea: "GN",
      "Guinea-Bissau": "GW",
      Guyana: "GY",
      Haiti: "HT",
      "Holy See": "VA",
      Honduras: "HN",
      Hungary: "HU",
      Iceland: "IS",
      India: "IN",
      Indonesia: "ID",
      Iran: "IR",
      Iraq: "IQ",
      Ireland: "IE",
      Israel: "IL",
      Italy: "IT",
      Jamaica: "JM",
      Japan: "JP",
      Jordan: "JO",
      Kazakhstan: "KZ",
      Kenya: "KE",
      Kuwait: "KW",
      Kyrgyzstan: "KG",
      Laos: "LA",
      Latvia: "LV",
      Lebanon: "LB",
      Lesotho: "LS",
      Liberia: "LR",
      Libya: "LY",
      Liechtenstein: "LI",
      Lithuania: "LT",
      Luxembourg: "LU",
      Madagascar: "MG",
      Malawi: "MW",
      Malaysia: "MY",
      Maldives: "MV",
      Mali: "ML",
      Malta: "MT",
      "Marshall Islands": "MH",
      Mauritania: "MR",
      Mauritius: "MU",
      Mexico: "MX",
      Micronesia: "FM",
      Moldova: "MD",
      Monaco: "MC",
      Mongolia: "MN",
      Montenegro: "ME",
      Morocco: "MA",
      Mozambique: "MZ",
      "Myanmar (Burma)": "MM",
      Namibia: "NA",
      Nauru: "NR",
      Nepal: "NP",
      Netherlands: "NL",
      "New Zealand": "NZ",
      Nicaragua: "NI",
      Niger: "NE",
      Nigeria: "NG",
      "North Korea": "KP",
      "North Macedonia": "MK",
      Norway: "NO",
      Oman: "OM",
      Pakistan: "PK",
      Palau: "PW",
      "Palestine State": "PS",
      Panama: "PA",
      "Papua New Guinea": "PG",
      Paraguay: "PY",
      Peru: "PE",
      Philippines: "PH",
      Poland: "PL",
      Portugal: "PT",
      Qatar: "QA",
      Romania: "RO",
      Russia: "RU",
      Rwanda: "RW",
      "Saint Kitts and Nevis": "KN",
      "Saint Lucia": "LC",
      "Saint Vincent and the Grenadines": "VC",
      Samoa: "WS",
      "San Marino": "SM",
      "Sao Tome and Principe": "ST",
      "Saudi Arabia": "SA",
      Senegal: "SN",
      Serbia: "RS",
      Seychelles: "SC",
      "Sierra Leone": "SL",
      Singapore: "SG",
      Slovakia: "SK",
      Slovenia: "SI",
      "Solomon Islands": "SB",
      Somalia: "SO",
      "South Africa": "ZA",
      "South Korea": "KR",
      "South Sudan": "SS",
      Spain: "ES",
      "Sri Lanka": "LK",
      Sudan: "SD",
      Suriname: "SR",
      Sweden: "SE",
      Switzerland: "CH",
      Syria: "SY",
      Tajikistan: "TJ",
      Tanzania: "TZ",
      Thailand: "TH",
      "Timor-Leste": "TL",
      Togo: "TG",
      Tonga: "TO",
      "Trinidad and Tobago": "TT",
      Tunisia: "TN",
      Turkey: "TR",
      Turkmenistan: "TM",
      Tuvalu: "TV",
      Uganda: "UG",
      Ukraine: "UA",
      "United Arab Emirates": "AE",
      Uruguay: "UY",
      Uzbekistan: "UZ",
      Vanuatu: "VU",
      Venezuela: "VE",
      Vietnam: "VN",
      Yemen: "YE",
      Zambia: "ZM",
      Zimbabwe: "ZW",
    };
    return countryToCode[countryName] || null;
  };

  // Get subdivision label based on country
  const getSubdivisionLabel = (countryName: string): string => {
    const code = getCountryCode(countryName);
    if (code === "US") return "State";
    if (code === "CA") return "Province";
    return "Region";
  };

  // Staging: mock approved identity so creator step 3 never opens Didit/Cloudflare.
  useEffect(() => {
    if (!stagingCreatorBypass || selectedType !== "model") {
      return;
    }
    setDiditVerificationData(STAGING_MOCK_DIDIT_VERIFICATION_DATA);
    setVerificationStatus(STAGING_MOCK_VERIFICATION_STATUS);
    sessionStorage.setItem(
      "diditVerificationStatus",
      STAGING_MOCK_VERIFICATION_STATUS
    );
    clearSessionUrl();
  }, [selectedType, clearSessionUrl]);

  // Poll Didit verification status every second for model users
  useEffect(() => {
    if (stagingCreatorBypass) {
      return;
    }

    let intervalId: NodeJS.Timeout | null = null;

    if (user?.id && selectedType === "model") {
      const pollVerificationStatus = async () => {
        try {
          const verificationData = await getDiditVerificationByUserIdAPI(
            user.id
          );

          if (verificationData?.didit_verifications?.[0]?.status) {
            const currentStatus = normalizeDiditStatus(
              verificationData.didit_verifications[0].status
            );
            sessionStorage.setItem("diditVerificationStatus", currentStatus);
            setVerificationStatus(currentStatus);

            if (lastPolledVerificationStatus.current !== currentStatus) {
              lastPolledVerificationStatus.current = currentStatus;
              if (isDiditFailureStatus(currentStatus)) {
                trackDiditVerificationFailureOutcome({
                  user_id: user.id,
                  status: currentStatus,
                  failure_source: "status_poll",
                });
              }
            }

            // Handle status-specific actions
            if (currentStatus === "approved") {
              // showToast("🎉 Your verification has been approved!", "success");
              // Clear any existing session URL since verification is complete
              clearSessionUrl();
              // setSessionUrl(undefined);
              // no-op
            } else if (currentStatus === "in_progress") {
              // Keep the iframe visible for in_progress status
              if (verificationData.didit_verifications[0].session_url) {
                setSessionUrl(
                  verificationData.didit_verifications[0].session_url
                );
                saveSessionUrl(
                  verificationData.didit_verifications[0].session_url
                );
                // no-op
              }
            } else if (currentStatus === "in_review") {
              // showToast(
              //   "🔍 Your verification is under review. Please wait for the result.",
              //   "info"
              // );
              // Clear session URL for in_review status
              clearSessionUrl();
              // setSessionUrl(undefined);
              // no-op
            } else if (
              currentStatus === "declined" ||
              currentStatus === "kyc_expired" ||
              currentStatus === "expired" ||
              currentStatus === "abandoned" ||
              currentStatus === "unknown" ||
              currentStatus === "pending"
            ) {
              // For these statuses, clear session data and allow restart
              // showToast(
              //   `❌ Your verification was ${currentStatus}. You can start a new verification.`,
              //   "error"
              // );
              // Clear session URL for these statuses
              clearSessionUrl();
              // setSessionUrl(undefined);
              // no-op
            }
          } else {
            // No verification data found, set to not_started
            setVerificationStatus("not_started");
            sessionStorage.setItem("diditVerificationStatus", "not_started");
          }

          setDiditVerificationData(verificationData);
        } catch (error) {
          console.error("Error fetching Didit verification status:", error);
          // On error, set to not_started to allow retry
          setVerificationStatus("not_started");
        }
      };

      // Call immediately
      pollVerificationStatus();

      // Set up interval to poll every second
      intervalId = setInterval(pollVerificationStatus, 3400);
    }

    // Cleanup interval on unmount or when dependencies change
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [selectedType, user?.id, clearSessionUrl, saveSessionUrl]);

  // Check Didit verification status for creators
  useEffect(() => {
    if (stagingCreatorBypass) {
      return;
    }

    // Get status from sessionStorage if available
    const savedStatus = sessionStorage.getItem("diditVerificationStatus");
    if (savedStatus) {
      setVerificationStatus(savedStatus as any);
      return;
    }

    // Set status based on verification data
    if (
      selectedType === "model" &&
      diditVerificationData?.didit_verifications?.[0]?.status
    ) {
      const currentStatus = normalizeDiditStatus(
        diditVerificationData.didit_verifications[0].status
      );
      setVerificationStatus(currentStatus);
      sessionStorage.setItem("diditVerificationStatus", currentStatus);

      // Handle session URL for in_progress status
      if (
        currentStatus === "in_progress" &&
        diditVerificationData.didit_verifications[0].session_url
      ) {
        setSessionUrl(diditVerificationData.didit_verifications[0].session_url);
        saveSessionUrl(
          diditVerificationData.didit_verifications[0].session_url
        );
        // no-op
      }
    } else if (selectedType === "model") {
      // No verification data found, set to not_started
      setVerificationStatus("not_started");
      sessionStorage.setItem("diditVerificationStatus", "not_started");
    }
  }, [diditVerificationData, selectedType, step, saveSessionUrl]);

  useEffect(() => {
    if (selectedType !== "model" || !verificationStatus) {
      return;
    }

    const normalizedStatus = normalizeDiditStatus(verificationStatus);

    if (
      normalizedStatus === "in_progress" &&
      !verificationStartedTracked.current
    ) {
      verificationStartedTracked.current = true;
      trackVerificationStarted({ user_id: user?.id });
      trackVerificationStepStarted({
        user_id: user?.id,
        step_type: "id_check",
      });
      if (getDiditVerificationAttemptCount() === 0) {
        registerDiditVerificationAttempt({
          user_id: user?.id,
          source: "auto_resume",
        });
      }
      return;
    }

    if (verificationCompletedTracked.current) {
      return;
    }

    if (isDiditSuccessStatus(normalizedStatus)) {
      verificationCompletedTracked.current = true;
      trackVerificationStepCompleted({
        user_id: user?.id,
        step_type: "id_check",
        result: "passed",
      });
      trackVerificationCompleted({
        user_id: user?.id,
        result: "approved",
        attempt_number: getDiditVerificationAttemptCount(),
      });
      return;
    }

    if (isDiditFailureStatus(normalizedStatus)) {
      verificationCompletedTracked.current = true;
      trackDiditVerificationFailureOutcome({
        user_id: user?.id,
        status: normalizedStatus,
        failure_source: "status_poll",
      });
    }
  }, [selectedType, verificationStatus, user?.id]);

  if (user?.isAdmin) {
    return <Redirect to="/" />;
  }

  const renderStepsSidebar = () => {
    const getSteps = () => {
      if (selectedType === "viewer") {
        return [
          { number: 1, label: "Choose your account type" },
          { number: 2, label: "Add your profile info" },
        ];
      } else {
        return [
          { number: 1, label: "Choose Account Type" },
          { number: 2, label: "Complete Profile" },
          { number: 3, label: "Identity Verification" },
        ];
      }
    };

    const steps = getSteps();

    const handleLogout = () => {
      resetMixpanelIdentity();
      clearTokens();
      dispatch(logoutUser());
      localStorage.removeItem("user");
      sessionStorage.clear();
      history.push("/login");
    };

    return (
      <Box
        className={creatorClasses.stepsSidebar}
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Box style={{ display: "flex", flexDirection: "column" }}>
            <img
              src={Logo}
              alt="Sugar Cribs logo"
              className={creatorClasses.sidebarLogo}
            />
            <Typography className={creatorClasses.sidebarText}>
              Create your account in a few clicks
            </Typography>
          </Box>
          {steps.map((stepItem, index) => {
            const isCompleted = step > stepItem.number;
            const isActive = step === stepItem.number;
            const stepNumber = String(stepItem.number).padStart(2, "0");
            const canNavigate = stepItem.number <= step;

            return (
              <Box key={stepItem.number}>
                <Box
                  style={{
                    borderRadius: "8px",
                    backgroundColor: "transparent",
                    color: isActive ? "#FFFFFF" : "#FFFFFF80",
                    fontSize: "14px",
                    fontWeight: isActive ? 600 : 400,
                    cursor: canNavigate ? "pointer" : "default",
                    transition: "all 0.3s ease",
                  }}
                  onClick={
                    canNavigate ? () => goToStep(stepItem.number) : undefined
                  }
                >
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <Box
                      style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "9999px",
                        backgroundColor: isCompleted
                          ? "#FE5A97"
                          : isActive
                          ? "transparent"
                          : "transparent",
                        border: isActive
                          ? "1px solid #FE5A97"
                          : isCompleted
                          ? "none"
                          : "1px solid #F7B4C766",
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: isActive
                          ? "#FFFFFF"
                          : isCompleted
                          ? "#000000"
                          : "#FFFFFF",
                        flexShrink: 0,
                      }}
                    >
                      {isCompleted ? (
                        <Check size={18} color="#000000" />
                      ) : (
                        stepNumber
                      )}
                    </Box>
                    <Typography
                      style={{
                        fontSize: "18px",
                        fontWeight: isActive || isCompleted ? 700 : 400,
                        color: "#FFFFFF",
                      }}
                    >
                      {stepItem.label}
                    </Typography>
                  </Box>
                </Box>
                {index < steps.length - 1 && (
                  <Box
                    style={{
                      width: "1px",
                      minHeight: "20px",
                      backgroundColor: "#FFFFFF1A",
                      marginLeft: "14px",
                      marginTop: "8px",
                      marginBottom: "8px",
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
        {/* Log out button for desktop only */}
        {!isMobile && (
          <Box
            onClick={handleLogout}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              marginTop: "auto",
              paddingTop: "24px",
            }}
          >
            <img
              src={ArrowBackRightIcon}
              alt="Log out"
              style={{
                width: "20px",
                height: "20px",
              }}
            />
            <Typography
              style={{
                fontSize: "20px",
                fontWeight: 400,
                color: "#FE5A97",
                backgroundColor: "transparent",
              }}
            >
              Log out
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  const renderMobileProgress = () => {
    const getSteps = () => {
      if (selectedType === "viewer") {
        return [
          { number: 1, label: "Choose your account type" },
          { number: 2, label: "Add your profile info" },
        ];
      } else {
        return [
          { number: 1, label: "Choose Account Type" },
          { number: 2, label: "Complete Profile" },
          { number: 3, label: "Identity Verification" },
        ];
      }
    };

    const steps = getSteps();

    return (
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          marginBottom: "24px",
          width: "100%",
        }}
      >
        {steps.map((stepItem, index) => {
          const isCompleted = step > stepItem.number;
          const isActive = step === stepItem.number;
          const stepNumber = String(stepItem.number).padStart(2, "0");
          const canNavigate = stepItem.number <= step;

          return (
            <Box
              key={stepItem.number}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: canNavigate ? "pointer" : "default",
              }}
              onClick={
                canNavigate ? () => goToStep(stepItem.number) : undefined
              }
            >
              <Box
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "9999px",
                  backgroundColor: isCompleted
                    ? "#FE5A97"
                    : isActive
                    ? "transparent"
                    : "transparent",
                  border: isActive
                    ? "1px solid #FE5A97"
                    : isCompleted
                    ? "none"
                    : "1px solid #F7B4C766",
                  boxSizing: "border-box",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: isActive
                    ? "#FFFFFF"
                    : isCompleted
                    ? "#000000"
                    : "#FFFFFF",
                  flexShrink: 0,
                }}
              >
                {isCompleted ? <Check size={18} color="#000000" /> : stepNumber}
              </Box>
              {index < steps.length - 1 && (
                <Box
                  style={{
                    width: "24px",
                    height: "1px",
                    backgroundColor: isCompleted ? "#FE5A97" : "#F7B4C766",
                    flexShrink: 0,
                  }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderStepContent = (content: React.ReactNode) => {
    if (!content) return null;
    if (step === 1) {
      return content;
    }
    if (isMobile) {
      // For mobile: show horizontal progress bar at the top
      return (
        <Box>
          {renderMobileProgress()}
          <Box className={creatorClasses.stepsContent}>{content}</Box>
        </Box>
      );
    }
    // For desktop: show sidebar layout
    return (
      <Box className={creatorClasses.stepsLayout}>
        {renderStepsSidebar()}
        <Box className={creatorClasses.stepsContent}>{content}</Box>
      </Box>
    );
  };

  return (
    <OnboardingLayout
      showHeader={step === 1}
      maxWidth={step === 1 ? 880 : 1195}
      hasSidebar={step > 1}
    >
      <StandardPageHelmet seo={seo} />
      <Box className={creatorClasses.container}>
        {step === 1 && (
          <>
            <Typography variant="h4" className={creatorClasses.title}>
              Choose <span>Account Type</span>
            </Typography>
            <Paper className={creatorClasses.selectionCard}>
              <Box className={creatorClasses.optionsContainer}>
                <Box
                  className={`${creatorClasses.optionCard} ${
                    selectedType === "model" ? creatorClasses.selectedCard : ""
                  }`}
                  onClick={() => setSelectedType("model")}
                >
                  <Box
                    className={`${creatorClasses.optionImageWrapper} ${
                      selectedType === "model"
                        ? creatorClasses.selectedImageWrapper
                        : ""
                    }`}
                  >
                    <img
                      src={CreatorLogo}
                      alt="Content Creator"
                      className={creatorClasses.optionImage}
                    />
                  </Box>
                  <Box className={creatorClasses.optionLabel}>
                    I want to create content
                  </Box>
                </Box>
                <Box
                  className={`${viewerClasses.optionCard} ${
                    selectedType === "viewer" ? viewerClasses.selectedCard : ""
                  }`}
                  onClick={() => setSelectedType("viewer")}
                >
                  <Box
                    className={`${viewerClasses.optionImageWrapper} ${
                      selectedType === "viewer"
                        ? viewerClasses.selectedImageWrapper
                        : ""
                    }`}
                  >
                    <img
                      src={ViewerLogo}
                      alt="Viewer"
                      className={creatorClasses.optionImage}
                    />
                  </Box>
                  <Box className={viewerClasses.optionLabel}>
                    I want to watch content
                  </Box>
                </Box>
              </Box>

              <Box className={creatorClasses.optionDivider} />

              <Box display="flex" justifyContent="flex-end" width="100%">
                <Box className={creatorClasses.buttonContainer}>
                  <Button
                    variant="contained"
                    className={creatorClasses.button}
                    disabled={!selectedType}
                    onClick={() => {
                      handleTypeChange(selectedType);
                    }}
                  >
                    Next
                  </Button>
                </Box>
              </Box>
            </Paper>
          </>
        )}

        {renderStepContent(
          step === 2 && selectedType === "viewer" && (
            <>
              <Typography variant="h4" className={profileStyle.title}>
                Complete Your Profile
              </Typography>
              <Typography
                style={{
                  display: "block",
                  width: "100%",
                  color: "#FFFFFF",
                  fontSize: isMobile ? "12px" : "16px",
                  fontWeight: isMobile ? 400 : 500,
                  marginBottom: "16px",
                  textAlign: isMobile ? "center" : "left",
                }}
              >
                Add information to get started and create a personalized
                experience
              </Typography>
              <Paper className={profileStyle.formCard}>
                <div>
                  {/* Upload Profile Image Button */}
                  <Box
                    onClick={() => setUploadMediaModalOpen(true)}
                    style={{
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                      marginBottom: "24px",
                    }}
                  >
                    {/* Avatar Circle */}
                    <Box
                      style={{
                        width: isMobile ? "68px" : "120px",
                        height: isMobile ? "68px" : "120px",
                        borderRadius: "100%",
                        backgroundColor: "#6C6C6C",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      {profileData.profileImg ? (
                        <img
                          src={
                            profileData.profileImg instanceof File
                              ? URL.createObjectURL(profileData.profileImg)
                              : profileData.profileImg
                          }
                          alt="Profile"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <img
                          src={CameraIcon}
                          alt="Camera"
                          style={{
                            width: "40px",
                            height: "40px",
                            opacity: 0.5,
                          }}
                        />
                      )}
                    </Box>

                    {/* Text Content */}
                    <Box style={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        style={{
                          fontSize: isMobile ? "16px" : "20px",
                          fontWeight: isMobile ? 700 : 600,
                          color: "#FFFFFF",
                          marginBottom: "4px",
                        }}
                      >
                        Upload profile image
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{
                          fontSize: isMobile ? "12px" : "20px",
                          fontWeight: 600,
                          color: "#FE5A97",
                        }}
                      >
                        Click to upload
                      </Typography>
                    </Box>
                  </Box>
                  {viewerErrors.profileImg && (
                    <Typography
                      variant="caption"
                      style={{
                        color: "#f44336",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {viewerErrors.profileImg}
                    </Typography>
                  )}

                  <Box style={{ marginTop: "24px" }}>
                    {/* Row 1: Name*, Surname* */}
                    <Box style={{ display: "flex", gap: "24px" }}>
                      <FormControl fullWidth className={classes.formControl}>
                        <InputLabel shrink className={classes.customLabel}>
                          Name*
                        </InputLabel>
                        <Input
                          placeholder="Enter First Name"
                          value={profileData.firstName}
                          className={classes.customInput}
                          onChange={e =>
                            handleViewerFieldChange("firstName", e.target.value)
                          }
                          onBlur={e =>
                            handleViewerFieldBlur("firstName", e.target.value)
                          }
                          error={!!viewerErrors.firstName}
                        />
                        {viewerErrors.firstName && (
                          <Typography
                            variant="caption"
                            style={{
                              color: "#f44336",
                              fontSize: "12px",
                              marginTop: "4px",
                            }}
                          >
                            {viewerErrors.firstName}
                          </Typography>
                        )}
                      </FormControl>

                      <FormControl fullWidth className={classes.formControl}>
                        <InputLabel shrink className={classes.customLabel}>
                          Surname*
                        </InputLabel>
                        <Input
                          placeholder="Enter Last Name"
                          value={profileData.lastName}
                          className={classes.customInput}
                          onChange={e =>
                            handleViewerFieldChange("lastName", e.target.value)
                          }
                          onBlur={e =>
                            handleViewerFieldBlur("lastName", e.target.value)
                          }
                          error={!!viewerErrors.lastName}
                        />
                        {viewerErrors.lastName && (
                          <Typography
                            variant="caption"
                            style={{
                              color: "#f44336",
                              fontSize: "12px",
                              marginTop: "4px",
                            }}
                          >
                            {viewerErrors.lastName}
                          </Typography>
                        )}
                      </FormControl>
                    </Box>

                    {/* Row 2: Username, Birth date* */}
                    <Box
                      style={{
                        display: "flex",
                        gap: "24px",
                        marginTop: "20px",
                      }}
                    >
                      <FormControl fullWidth className={classes.formControl}>
                        <InputLabel shrink className={classes.customLabel}>
                          Username
                        </InputLabel>
                        <Input
                          placeholder="Enter Username"
                          value={profileData.username}
                          className={classes.customInput}
                          onChange={e =>
                            handleViewerFieldChange("username", e.target.value)
                          }
                          onBlur={e =>
                            handleViewerFieldBlur("username", e.target.value)
                          }
                          error={!!viewerErrors.username}
                          endAdornment={
                            profileData.username.trim().length >= 3 && (
                              <InputAdornment position="end">
                                {isCheckingUsername ? (
                                  <Typography
                                    variant="caption"
                                    style={{
                                      color: "#A3A3A3",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Checking...
                                  </Typography>
                                ) : usernameAvailable === true ? (
                                  <Typography
                                    variant="caption"
                                    style={{
                                      color: "#4caf50",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Available
                                  </Typography>
                                ) : usernameAvailable === false ? (
                                  <Typography
                                    variant="caption"
                                    style={{
                                      color: "#f44336",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Taken
                                  </Typography>
                                ) : null}
                              </InputAdornment>
                            )
                          }
                        />
                        {viewerErrors.username && (
                          <Typography
                            variant="caption"
                            style={{
                              color: "#f44336",
                              fontSize: "12px",
                              marginTop: "4px",
                            }}
                          >
                            {viewerErrors.username}
                          </Typography>
                        )}
                        {!viewerErrors.username &&
                          profileData.username.trim().length >= 3 &&
                          usernameAvailable === true && (
                            <Typography
                              variant="caption"
                              style={{
                                color: "#4caf50",
                                fontSize: "12px",
                                marginTop: "4px",
                              }}
                            >
                              Username is available
                            </Typography>
                          )}
                      </FormControl>

                      <FormControl fullWidth className={classes.formControl}>
                        <InputLabel
                          shrink
                          className={classes.customLabel}
                          style={{ color: "#FFFFFF" }}
                        >
                          Birth date*
                        </InputLabel>
                        <Input
                          type="date"
                          value={profileData.DOB}
                          className={classes.customInput}
                          startAdornment={
                            <InputAdornment position="start">
                              <CalendarToday style={{ fontSize: 18 }} />
                            </InputAdornment>
                          }
                          onChange={e =>
                            handleViewerFieldChange("DOB", e.target.value)
                          }
                          onBlur={e =>
                            handleViewerFieldBlur("DOB", e.target.value)
                          }
                          error={!!viewerErrors.DOB}
                          inputProps={{
                            style: {
                              cursor: "pointer",
                              color: profileData.DOB ? "#FFFFFF" : "#FFFFFF",
                            },
                            placeholder: "Select your birth date",
                            onClick: (e: any) => {
                              // Allow clicking on the input to open date picker
                              e.target.showPicker?.();
                            },
                          }}
                        />
                        {viewerErrors.DOB && (
                          <Typography
                            variant="caption"
                            style={{
                              color: "#f44336",
                              fontSize: "12px",
                              marginTop: "4px",
                            }}
                          >
                            {viewerErrors.DOB}
                          </Typography>
                        )}
                      </FormControl>
                    </Box>

                    {/* Row 3: Country, State/Province (if applicable), City (Input) */}
                    {(() => {
                      const countryCode = profileData.country
                        ? getCountryCode(profileData.country)
                        : null;
                      const hasSubdivisions =
                        countryCode && SUBDIVISIONS[countryCode];
                      return (
                        <>
                          <Box
                            style={{
                              display: "flex",
                              gap: "24px",
                              marginTop: "20px",
                            }}
                          >
                            <FormControl fullWidth className={classes.formControl}>
                              <InputLabel shrink className={classes.customLabel}>
                                Country
                              </InputLabel>
                              <Select
                                value={profileData.country || ""}
                                onChange={e => {
                                  const newCountry = e.target.value as string;
                                  setProfileData(prev => ({
                                    ...prev,
                                    country: newCountry,
                                    subdivision: "", // Reset subdivision when country changes
                                    city: "", // Reset city when country changes
                                  }));
                                }}
                                displayEmpty
                                IconComponent={ArrowDropDownIcon}
                                className={classes.customInput}
                                MenuProps={{
                                  getContentAnchorEl: null,
                                  anchorOrigin: {
                                    vertical: "bottom",
                                    horizontal: "left",
                                  },
                                  transformOrigin: {
                                    vertical: "top",
                                    horizontal: "left",
                                  },
                                  PaperProps: {
                                    style: {
                                      maxHeight: 300,
                                      backgroundColor: "#fff",
                                    },
                                  },
                                }}
                              >
                                {[
                                  "United States",
                                  "United Kingdom",
                                  "Canada",
                                  "Australia",
                                ].map(c => (
                                  <MenuItem
                                    key={c}
                                    value={c}
                                    style={{ color: "#000" }}
                                  >
                                    <span style={{ marginRight: 8 }}>
                                      {countryToFlag(c)}
                                    </span>
                                    {c}
                                  </MenuItem>
                                ))}
                                <MenuItem
                                  disabled
                                  value="__sep__"
                                  style={{ color: "#999" }}
                                >
                                  ———————————————
                                </MenuItem>
                                {COUNTRIES.filter(
                                  c =>
                                    ![
                                      "United States",
                                      "United Kingdom",
                                      "Canada",
                                      "Australia",
                                    ].includes(c)
                                ).map(c => (
                                  <MenuItem
                                    key={c}
                                    value={c}
                                    style={{ color: "#000" }}
                                  >
                                    <span style={{ marginRight: 8 }}>
                                      {countryToFlag(c)}
                                    </span>
                                    {c}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            {/* State/Province Select - shown when country has subdivisions */}
                            {hasSubdivisions ? (
                              <FormControl fullWidth className={classes.formControl}>
                                <InputLabel shrink className={classes.customLabel}>
                                  State/Province
                                </InputLabel>
                                <Select
                                  value={profileData.subdivision || ""}
                                  onChange={e =>
                                    setProfileData(prev => ({
                                      ...prev,
                                      subdivision: e.target.value as string,
                                    }))
                                  }
                                  displayEmpty
                                  IconComponent={ArrowDropDownIcon}
                                  className={classes.customInput}
                                  MenuProps={{
                                    getContentAnchorEl: null,
                                    anchorOrigin: {
                                      vertical: "bottom",
                                      horizontal: "left",
                                    },
                                    transformOrigin: {
                                      vertical: "top",
                                      horizontal: "left",
                                    },
                                    PaperProps: {
                                      style: {
                                        maxHeight: 300,
                                        backgroundColor: "#fff",
                                      },
                                    },
                                  }}
                                >
                                  <MenuItem value="" style={{ color: "#000" }}>
                                    <em>Select State/Province</em>
                                  </MenuItem>
                                  {SUBDIVISIONS[countryCode!].map(subdivision => (
                                    <MenuItem
                                      key={subdivision}
                                      value={subdivision}
                                      style={{ color: "#000" }}
                                    >
                                      {subdivision}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : (
                              /* City Input - shown in same row when no subdivisions */
                              <FormControl fullWidth className={classes.formControl}>
                                <InputLabel shrink className={classes.customLabel}>
                                  City
                                </InputLabel>
                                <Input
                                  value={profileData.city || ""}
                                  onChange={e =>
                                    setProfileData(prev => ({
                                      ...prev,
                                      city: e.target.value as string,
                                    }))
                                  }
                                  className={classes.customInput}
                                  placeholder="Enter city"
                                />
                              </FormControl>
                            )}
                          </Box>

                          {/* City Input - shown below when subdivisions exist */}
                          {hasSubdivisions && (
                            <Box
                              style={{
                                marginTop: "20px",
                                marginRight: 20,
                                display: "flex",
                              }}
                            >
                              <FormControl className={classes.formControl} style={{ width: "50%",}}>
                                <InputLabel shrink className={classes.customLabel}>
                                  City
                                </InputLabel>
                                <Input
                                  value={profileData.city || ""}
                                  onChange={e =>
                                    setProfileData(prev => ({
                                      ...prev,
                                      city: e.target.value as string,
                                    }))
                                  }
                                  className={classes.customInput}
                                  placeholder="Enter city"
                                />
                              </FormControl>
                            </Box>
                          )}
                        </>
                      );
                    })()}

                    {/* Bio Field */}
                    <FormControl
                      fullWidth
                      style={{ marginTop: 40 }}
                      className={classes.formControl}
                    >
                      <InputLabel shrink className={classes.customLabel}>
                        Bio
                      </InputLabel>
                      <Input
                        placeholder="Tell other users more about yourself"
                        value={profileData.bio}
                        className={classes.customInput}
                        onChange={e =>
                          handleViewerFieldChange("bio", e.target.value)
                        }
                        onBlur={e =>
                          handleViewerFieldBlur("bio", e.target.value)
                        }
                        error={!!viewerErrors.bio}
                      />
                      {viewerErrors.bio && (
                        <Typography
                          variant="caption"
                          style={{
                            color: "#f44336",
                            fontSize: "12px",
                            marginTop: "4px",
                          }}
                        >
                          {viewerErrors.bio}
                        </Typography>
                      )}
                    </FormControl>
                  </Box>
                </div>

                <Box
                  marginTop="60px"
                  display="flex"
                  justifyContent="space-between"
                  width="100%"
                >
                  <Button
                    variant="outlined"
                    className={creatorClasses.button}
                    onClick={() => prevStep()}
                    style={{
                      backgroundColor: "transparent",
                      border: "1px solid #FE5A97",
                      color: "#FFFFFF",
                    }}
                  >
                    Back
                  </Button>

                  <Box className={creatorClasses.buttonContainer}>
                    <Button
                      variant="contained"
                      className={creatorClasses.button}
                      disabled={!isViewerFormValid()}
                      onClick={handleProfileChange}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </>
          )
        )}
        {renderStepContent(
          step === 2 && selectedType === "model" && (
            <>
              <Typography variant="h4" className={profileStyle.title}>
                Complete Your Profile
              </Typography>
              <Typography
                style={{
                  display: "block",
                  width: "100%",
                  color: "#FFFFFF",
                  fontSize: isMobile ? "12px" : "16px",
                  fontWeight: isMobile ? 400 : 500,
                  marginBottom: "16px",
                  textAlign: isMobile ? "center" : "left",
                }}
              >
                Add information to get started and create a personalized
                experience
              </Typography>
              <Paper className={profileStyle.formCard}>
                <div>
                  {/* Upload Profile Image Button */}
                  <Box
                    onClick={() => setUploadMediaModalOpen(true)}
                    style={{
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                      marginBottom: "24px",
                    }}
                  >
                    {/* Avatar Circle */}
                    <Box
                      style={{
                        width: isMobile ? "68px" : "120px",
                        height: isMobile ? "68px" : "120px",
                        borderRadius: "100%",
                        backgroundColor: "#6C6C6C",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      {profileData.profileImg ? (
                        <img
                          src={
                            profileData.profileImg instanceof File
                              ? URL.createObjectURL(profileData.profileImg)
                              : profileData.profileImg
                          }
                          alt="Profile"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <img
                          src={CameraIcon}
                          alt="Camera"
                          style={{
                            width: "40px",
                            height: "40px",
                            opacity: 0.5,
                          }}
                        />
                      )}
                    </Box>

                    {/* Text Content */}
                    <Box style={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        style={{
                          fontSize: isMobile ? "16px" : "20px",
                          fontWeight: isMobile ? 700 : 600,
                          color: "#FFFFFF",
                          marginBottom: "4px",
                        }}
                      >
                        Upload profile image
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{
                          fontSize: isMobile ? "12px" : "20px",
                          fontWeight: 600,
                          color: "#FE5A97",
                        }}
                      >
                        Click to upload
                      </Typography>
                    </Box>
                  </Box>
                  {creatorErrors.profileImg && (
                    <Typography
                      variant="caption"
                      style={{
                        color: "#f44336",
                        fontSize: "12px",
                        marginTop: "4px",
                      }}
                    >
                      {creatorErrors.profileImg}
                    </Typography>
                  )}

                  <Box style={{ marginTop: "24px" }}>
                    {/* Row 1: Name*, Surname* */}
                    <Box style={{ display: "flex", gap: "24px" }}>
                      <FormControl fullWidth className={classes.formControl}>
                        <InputLabel shrink className={classes.customLabel}>
                          Name*
                        </InputLabel>
                        <Input
                          placeholder="Enter First Name"
                          value={profileData.firstName}
                          className={classes.customInput}
                          onChange={e =>
                            handleCreatorFieldChange(
                              "firstName",
                              e.target.value
                            )
                          }
                          onBlur={e =>
                            handleCreatorFieldBlur("firstName", e.target.value)
                          }
                          error={!!creatorErrors.firstName}
                        />
                        {creatorErrors.firstName && (
                          <Typography
                            variant="caption"
                            style={{
                              color: "#f44336",
                              fontSize: "12px",
                              marginTop: "4px",
                            }}
                          >
                            {creatorErrors.firstName}
                          </Typography>
                        )}
                      </FormControl>

                      <FormControl fullWidth className={classes.formControl}>
                        <InputLabel shrink className={classes.customLabel}>
                          Surname*
                        </InputLabel>
                        <Input
                          placeholder="Enter Last Name"
                          value={profileData.lastName}
                          className={classes.customInput}
                          onChange={e =>
                            handleCreatorFieldChange("lastName", e.target.value)
                          }
                          onBlur={e =>
                            handleCreatorFieldBlur("lastName", e.target.value)
                          }
                          error={!!creatorErrors.lastName}
                        />
                        {creatorErrors.lastName && (
                          <Typography
                            variant="caption"
                            style={{
                              color: "#f44336",
                              fontSize: "12px",
                              marginTop: "4px",
                            }}
                          >
                            {creatorErrors.lastName}
                          </Typography>
                        )}
                      </FormControl>
                    </Box>

                    {/* Row 2: Username, Birth date* */}
                    <Box
                      style={{
                        display: "flex",
                        gap: "24px",
                        marginTop: "20px",
                      }}
                    >
                      <FormControl fullWidth className={classes.formControl}>
                        <InputLabel shrink className={classes.customLabel}>
                          Username
                        </InputLabel>
                        <Input
                          placeholder="Enter Username"
                          value={profileData.username}
                          className={classes.customInput}
                          onChange={e =>
                            handleCreatorFieldChange("username", e.target.value)
                          }
                          onBlur={e =>
                            handleCreatorFieldBlur("username", e.target.value)
                          }
                          error={!!creatorErrors.username}
                          endAdornment={
                            profileData.username.trim().length >= 3 && (
                              <InputAdornment position="end">
                                {isCheckingUsername ? (
                                  <Typography
                                    variant="caption"
                                    style={{
                                      color: "#A3A3A3",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Checking...
                                  </Typography>
                                ) : usernameAvailable === true ? (
                                  <Typography
                                    variant="caption"
                                    style={{
                                      color: "#4caf50",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Available
                                  </Typography>
                                ) : usernameAvailable === false ? (
                                  <Typography
                                    variant="caption"
                                    style={{
                                      color: "#f44336",
                                      fontSize: "12px",
                                    }}
                                  >
                                    Taken
                                  </Typography>
                                ) : null}
                              </InputAdornment>
                            )
                          }
                        />
                        {creatorErrors.username && (
                          <Typography
                            variant="caption"
                            style={{
                              color: "#f44336",
                              fontSize: "12px",
                              marginTop: "4px",
                            }}
                          >
                            {creatorErrors.username}
                          </Typography>
                        )}
                        {!creatorErrors.username &&
                          profileData.username.trim().length >= 3 &&
                          usernameAvailable === true && (
                            <Typography
                              variant="caption"
                              style={{
                                color: "#4caf50",
                                fontSize: "12px",
                                marginTop: "4px",
                              }}
                            >
                              Username is available
                            </Typography>
                          )}
                      </FormControl>

                      <FormControl fullWidth className={classes.formControl}>
                        <InputLabel
                          shrink
                          className={classes.customLabel}
                          style={{ color: "#FFFFFF" }}
                        >
                          Birth date*
                        </InputLabel>
                        <Input
                          type="date"
                          value={profileData.DOB}
                          className={classes.customInput}
                          startAdornment={
                            <InputAdornment position="start">
                              <CalendarToday style={{ fontSize: 18 }} />
                            </InputAdornment>
                          }
                          onChange={e =>
                            handleCreatorFieldChange("DOB", e.target.value)
                          }
                          onBlur={e =>
                            handleCreatorFieldBlur("DOB", e.target.value)
                          }
                          error={!!creatorErrors.DOB}
                          inputProps={{
                            style: {
                              cursor: "pointer",
                              color: profileData.DOB ? "#FFFFFF" : "#FFFFFF",
                            },
                            placeholder: "Select your birth date",
                            onClick: (e: any) => {
                              // Allow clicking on the input to open date picker
                              e.target.showPicker?.();
                            },
                          }}
                        />
                        {creatorErrors.DOB && (
                          <Typography
                            variant="caption"
                            style={{
                              color: "#f44336",
                              fontSize: "12px",
                              marginTop: "4px",
                            }}
                          >
                            {creatorErrors.DOB}
                          </Typography>
                        )}
                      </FormControl>
                    </Box>

                    {/* Row 3: Country, State/Province (if applicable), City (Input) */}
                    {(() => {
                      const countryCode = profileData.country
                        ? getCountryCode(profileData.country)
                        : null;
                      const hasSubdivisions =
                        countryCode && SUBDIVISIONS[countryCode];
                      return (
                        <>
                          <Box
                            style={{
                              display: "flex",
                              gap: "24px",
                              marginTop: "20px",
                            }}
                          >
                            <FormControl fullWidth className={classes.formControl}>
                              <InputLabel shrink className={classes.customLabel}>
                                Country
                              </InputLabel>
                              <Select
                                value={profileData.country || ""}
                                onChange={e => {
                                  const newCountry = e.target.value as string;
                                  setProfileData(prev => ({
                                    ...prev,
                                    country: newCountry,
                                    subdivision: "", // Reset subdivision when country changes
                                    city: "", // Reset city when country changes
                                  }));
                                }}
                                displayEmpty
                                IconComponent={ArrowDropDownIcon}
                                className={classes.customInput}
                                MenuProps={{
                                  getContentAnchorEl: null,
                                  anchorOrigin: {
                                    vertical: "bottom",
                                    horizontal: "left",
                                  },
                                  transformOrigin: {
                                    vertical: "top",
                                    horizontal: "left",
                                  },
                                  PaperProps: {
                                    style: {
                                      maxHeight: 300,
                                      backgroundColor: "#fff",
                                    },
                                  },
                                }}
                              >
                                {[
                                  "United States",
                                  "United Kingdom",
                                  "Canada",
                                  "Australia",
                                ].map(c => (
                                  <MenuItem
                                    key={c}
                                    value={c}
                                    style={{ color: "#000" }}
                                  >
                                    <span style={{ marginRight: 8 }}>
                                      {countryToFlag(c)}
                                    </span>
                                    {c}
                                  </MenuItem>
                                ))}
                                <MenuItem
                                  disabled
                                  value="__sep__"
                                  style={{ color: "#999" }}
                                >
                                  ———————————————
                                </MenuItem>
                                {COUNTRIES.filter(
                                  c =>
                                    ![
                                      "United States",
                                      "United Kingdom",
                                      "Canada",
                                      "Australia",
                                    ].includes(c)
                                ).map(c => (
                                  <MenuItem
                                    key={c}
                                    value={c}
                                    style={{ color: "#000" }}
                                  >
                                    <span style={{ marginRight: 8 }}>
                                      {countryToFlag(c)}
                                    </span>
                                    {c}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            {/* State/Province Select - shown when country has subdivisions */}
                            {hasSubdivisions ? (
                              <FormControl fullWidth className={classes.formControl}>
                                <InputLabel shrink className={classes.customLabel}>
                                  State/Province
                                </InputLabel>
                                <Select
                                  value={profileData.subdivision || ""}
                                  onChange={e =>
                                    setProfileData(prev => ({
                                      ...prev,
                                      subdivision: e.target.value as string,
                                    }))
                                  }
                                  displayEmpty
                                  IconComponent={ArrowDropDownIcon}
                                  className={classes.customInput}
                                  MenuProps={{
                                    getContentAnchorEl: null,
                                    anchorOrigin: {
                                      vertical: "bottom",
                                      horizontal: "left",
                                    },
                                    transformOrigin: {
                                      vertical: "top",
                                      horizontal: "left",
                                    },
                                    PaperProps: {
                                      style: {
                                        maxHeight: 300,
                                        backgroundColor: "#fff",
                                      },
                                    },
                                  }}
                                >
                                  <MenuItem value="" style={{ color: "#000" }}>
                                    <em>Select State/Province</em>
                                  </MenuItem>
                                  {SUBDIVISIONS[countryCode!].map(subdivision => (
                                    <MenuItem
                                      key={subdivision}
                                      value={subdivision}
                                      style={{ color: "#000" }}
                                    >
                                      {subdivision}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            ) : (
                              /* City Input - shown in same row when no subdivisions */
                              <FormControl fullWidth className={classes.formControl}>
                                <InputLabel shrink className={classes.customLabel}>
                                  City
                                </InputLabel>
                                <Input
                                  value={profileData.city || ""}
                                  onChange={e =>
                                    setProfileData(prev => ({
                                      ...prev,
                                      city: e.target.value as string,
                                    }))
                                  }
                                  className={classes.customInput}
                                  placeholder="Enter city"
                                />
                              </FormControl>
                            )}
                          </Box>

                          {/* City Input - shown below when subdivisions exist */}
                          {hasSubdivisions && (
                            <Box
                              style={{
                                marginTop: "20px",
                                display: "flex",
                              }}
                            >
                              <FormControl className={classes.formControl} style={{ width: "50%" }}>
                                <InputLabel shrink className={classes.customLabel}>
                                  City
                                </InputLabel>
                                <Input
                                  value={profileData.city || ""}
                                  onChange={e =>
                                    setProfileData(prev => ({
                                      ...prev,
                                      city: e.target.value as string,
                                    }))
                                  }
                                  className={classes.customInput}
                                  placeholder="Enter city"
                                />
                              </FormControl>
                            </Box>
                          )}
                        </>
                      );
                    })()}

                    {/* Bio Field */}
                    <FormControl
                      fullWidth
                      style={{ marginTop: 40 }}
                      className={classes.formControl}
                    >
                      <InputLabel shrink className={classes.customLabel}>
                        Bio
                      </InputLabel>
                      <Input
                        placeholder="Tell other users more about yourself"
                        value={profileData.bio}
                        className={classes.customInput}
                        onChange={e =>
                          handleCreatorFieldChange("bio", e.target.value)
                        }
                        onBlur={e =>
                          handleCreatorFieldBlur("bio", e.target.value)
                        }
                        error={!!creatorErrors.bio}
                      />
                      {creatorErrors.bio && (
                        <Typography
                          variant="caption"
                          style={{
                            color: "#f44336",
                            fontSize: "12px",
                            marginTop: "4px",
                          }}
                        >
                          {creatorErrors.bio}
                        </Typography>
                      )}
                    </FormControl>
                  </Box>
                </div>

                <Box
                  display="flex"
                  justifyContent="space-between"
                  width="100%"
                  style={{ marginTop: "60px" }}
                >
                  <Button
                    variant="outlined"
                    className={creatorClasses.button}
                    onClick={() => prevStep()}
                    style={{
                      backgroundColor: "transparent",
                      border: "1px solid #FE5A97",
                      color: "#FFFFFF",
                    }}
                  >
                    Back
                  </Button>

                  <Box className={creatorClasses.buttonContainer}>
                    <Button
                      variant="contained"
                      className={creatorClasses.button}
                      disabled={!isCreatorFormValid()}
                      onClick={handleProfileChange}
                    >
                      Next
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </>
          )
        )}
        {renderStepContent(
          step === 3 && selectedType === "model" && (
            <>
              <Paper
                className={profileStyle.formCard}
                style={{ maxWidth: 838 }}
              >
                {/* Check if verification has started */}
                {(() => {
                  const verificationStatus =
                    diditVerificationData?.didit_verifications?.[0]?.status ||
                    "Not Started";
                  const isVerificationStarted =
                    sessionUrl ||
                    verificationStatus === "In Progress" ||
                    verificationStatus === "Pending";
                  const isVerificationApproved =
                    verificationStatus === "Approved";
                  const isVerificationPending =
                    (verificationStatus === "Pending" ||
                      verificationStatus === "In Progress") &&
                    !sessionUrl;
                  const isVerificationUnsuccessful =
                    verificationStatus !== "Approved" &&
                    verificationStatus !== "Not Started" &&
                    verificationStatus !== "In Progress" &&
                    verificationStatus !== "Pending";

                  // If verification unsuccessful, show failure screen
                  if (isVerificationUnsuccessful) {
                    return (
                      <Box
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        {/* Unsuccessful Icon */}
                        <img
                          src={UnsuccessfulVerificationIcon}
                          alt="Verification Unsuccessful"
                          style={{
                            width: isMobile ? "120px" : "200px",
                            height: "auto",
                            marginBottom: isMobile ? "16px" : "20px",
                            transform: "scale(0.8)",
                          }}
                        />

                        {/* Title */}
                        <Typography
                          style={{
                            fontSize: isMobile ? "20px" : "32px",
                            fontWeight: 600,
                            color: "#FFFFFF",
                            textAlign: "center",
                            marginBottom: isMobile ? "16px" : "20px",
                            width: "100%",
                          }}
                        >
                          Verification unsuccessful
                        </Typography>

                        {/* Subtitle */}
                        <Typography
                          style={{
                            fontSize: isMobile ? "14px" : "20px",
                            fontWeight: 400,
                            color: "#FFFFFFCC",
                            textAlign: "center",
                            marginBottom: "32px",
                            maxWidth: "600px",
                            width: "100%",
                            lineHeight: 1.5,
                          }}
                        >
                          We couldn't complete your verification this time.
                          Don't worry — we'll review it again, and if your
                          documents meet the requirements, you'll receive an
                          email notification soon.
                        </Typography>

                        {/* Try Again Button */}
                        <Button
                          variant="outlined"
                          className={creatorClasses.button}
                          onClick={() => {
                            // Restart verification
                            startDiditVerification();
                          }}
                          style={{
                            backgroundColor: "transparent",
                            border: "1px solid #FE5A97",
                            color: "#fff",
                            width: "100%",
                            maxWidth: "400px",
                            fontSize: isMobile ? "14px" : "20px",
                            fontWeight: isMobile ? 500 : 600,
                          }}
                        >
                          Try verification again
                        </Button>
                      </Box>
                    );
                  }

                  // If verification approved, show success screen
                  if (isVerificationApproved) {
                    return (
                      <Box
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        {/* Success Icon */}
                        <img
                          src={SuccessfulVerificationIcon}
                          alt="Successful Verification"
                          style={{
                            width: isMobile ? "120px" : "200px",
                            height: "auto",
                            marginBottom: isMobile ? "20px" : "25px",
                            transform: "scale(0.8)",
                          }}
                        />

                        {/* Title */}
                        <Typography
                          style={{
                            fontSize: isMobile ? "20px" : "32px",
                            fontWeight: 600,
                            color: "#FFFFFF",
                            textAlign: "center",
                            marginBottom: isMobile ? "16px" : "20px",
                            width: "100%",
                          }}
                        >
                          Successful Verification
                        </Typography>

                        {/* Subtitle */}
                        <Typography
                          style={{
                            fontSize: isMobile ? "14px" : "20px",
                            fontWeight: 400,
                            color: "#FFFFFFCC",
                            textAlign: "center",
                            marginBottom: "32px",
                            maxWidth: "768px",
                            width: "100%",
                          }}
                        >
                          One last step before we continue — please review and
                          agree to our rules.
                        </Typography>

                        {/* Checkboxes */}
                        <Box
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "6px",
                            width: "100%",
                            marginBottom: "32px",
                            // alignItems: "center",
                            maxWidth: 500,
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={agreementAccepted}
                                onChange={e =>
                                  setAgreementAccepted(e.target.checked)
                                }
                                style={{
                                  color: "#FFFFFF",
                                }}
                              />
                            }
                            label={
                              <Typography
                                style={{
                                  color: "#FFFFFF",
                                  fontSize: isMobile ? "12px" : "20px",
                                  fontWeight: 400,
                                }}
                              >
                                I agree to the{" "}
                                <a
                                  href="/content-provider-agreement"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{
                                    color: "#FFFFFF",
                                    fontWeight: "bold",
                                    textDecoration: "underline",
                                  }}
                                >
                                  Content Provider Agreement
                                </a>
                              </Typography>
                            }
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={imageUsageAgreed}
                                onChange={e =>
                                  setImageUsageAgreed(e.target.checked)
                                }
                                style={{
                                  color: "#FFFFFF",
                                }}
                              />
                            }
                            label={
                              <Typography
                                style={{
                                  color: "#FFFFFF",
                                  fontSize: isMobile ? "12px" : "20px",
                                  fontWeight: 400,
                                  whiteSpace: "pre-line",
                                }}
                              >
                                I understand and agree that my images may{"\n"}
                                be used and publicly displayed on the website.
                              </Typography>
                            }
                          />
                        </Box>

                        {/* Complete Onboarding Button */}
                        <Button
                          variant="contained"
                          className={creatorClasses.button}
                          disabled={!agreementAccepted || !imageUsageAgreed}
                          onClick={async () => {
                            if (!agreementAccepted || !imageUsageAgreed) return;
                            try {
                              setIsFinalizing(true);
                              const res: any = await editProfileDataAPI({
                                isOnboarding: true,
                                isOnboaring: true,
                                validated: true,
                                contentProviderAgreementAccepted: true,
                                ...getCreatorOnboardingFinalizeFields(),
                              });
                              if (res?.user && user) {
                                dispatch(
                                  setUser({
                                    ...user,
                                    ...res.user,
                                    ...(stagingCreatorBypass
                                      ? { ageProofed: true }
                                      : {}),
                                  })
                                );
                              }
                              showToast("Onboarding completed!", "success");
                              clearSavedStep();
                              trackOnboardingCompleted({
                                account_type: "model",
                                user_id: user?.id,
                              });
                              history.push("/");
                            } catch (e: any) {
                              showToast(
                                e?.message || "Failed to complete onboarding",
                                "error"
                              );
                            } finally {
                              setIsFinalizing(false);
                            }
                          }}
                          style={{
                            width: "100%",
                            maxWidth: "400px",
                            fontSize: isMobile ? "14px" : "20px",
                            fontWeight: isMobile ? 500 : 600,
                          }}
                        >
                          {isFinalizing
                            ? "Completing..."
                            : "Start your Sugar Cribs journey"}
                        </Button>
                      </Box>
                    );
                  }

                  // If verification pending (no active session), show pending screen
                  if (isVerificationPending) {
                    return (
                      <Box
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          width: "100%",
                        }}
                      >
                        {/* Pending Icon */}
                        <img
                          src={PendingVerificationIcon}
                          alt="Pending Verification"
                          style={{
                            width: isMobile ? "120px" : "200px",
                            height: "auto",
                            marginBottom: isMobile ? "16px" : "20px",
                            transform: "scale(0.8)",
                          }}
                        />

                        {/* Title */}
                        <Typography
                          style={{
                            fontSize: isMobile ? "20px" : "32px",
                            fontWeight: 600,
                            color: "#FFFFFF",
                            textAlign: "center",
                            marginBottom: isMobile ? "16px" : "20px",
                            width: "100%",
                          }}
                        >
                          Pending Verification
                        </Typography>

                        {/* Subtitle */}
                        <Typography
                          style={{
                            fontSize: isMobile ? "14px" : "20px",
                            fontWeight: 400,
                            color: "#FFFFFFCC",
                            textAlign: "center",
                            maxWidth: "600px",
                            width: "100%",
                            lineHeight: 1.5,
                          }}
                        >
                          We're taking a bit more time to review your ID.
                        </Typography>

                        <Typography
                          style={{
                            fontSize: isMobile ? "14px" : "20px",
                            fontWeight: 400,
                            color: "#FFFFFFCC",
                            textAlign: "center",
                            maxWidth: "600px",
                            marginBottom: "12px",
                            width: "100%",
                            lineHeight: 1.5,
                          }}
                        >
                          You'll get an email as soon as it's done.
                        </Typography>

                        <Typography
                          style={{
                            fontSize: isMobile ? "14px" : "20px",
                            fontWeight: 400,
                            color: "#FFFFFFCC",
                            textAlign: "center",
                            marginBottom: "32px",
                            maxWidth: "600px",
                            width: "100%",
                            lineHeight: 1.5,
                          }}
                        >
                          No response in 48 hours? Drop us an email!
                          <br />
                          <a
                            href="mailto:support@sugarcribs.com"
                            style={{
                              color: "#FF78AA",
                              textDecoration: "underline",
                            }}
                          >
                            support@sugarcribs.com
                          </a>
                        </Typography>
                      </Box>
                    );
                  }

                  // If verification started, show only iframe and buttons
                  if (isVerificationStarted) {
                    return (
                      <>
                        {/* Show iframe for in_progress status */}
                        {sessionUrl && (
                          <Box
                            style={{
                              margin: "20px 0",
                              border: "2px solid #6941C6",
                              borderRadius: "12px",
                              overflow: "hidden",
                              backgroundColor: "#fff",
                            }}
                          >
                            <Box
                              style={{
                                backgroundColor: "#6941C6",
                                color: "white",
                                padding: "12px 16px",
                                fontSize: "14px",
                                fontWeight: "bold",
                              }}
                            >
                              🔐 Identity Verification
                            </Box>
                            <iframe
                              src={sessionUrl}
                              width="100%"
                              height="600"
                              frameBorder="0"
                              style={{
                                display: "block",
                                border: "none",
                              }}
                              title="Didit Identity Verification"
                              sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                            />
                            <Box
                              style={{
                                backgroundColor: "#f8f9fa",
                                padding: "12px 16px",
                                fontSize: "12px",
                                color: "#666",
                                borderTop: "1px solid #e9ecef",
                              }}
                            >
                              💡 Complete the verification steps above. Once
                              finished, you'll be able to proceed to the next
                              step and start your creator journey!
                            </Box>
                          </Box>
                        )}

                        {/* Popup blocked message */}
                        {popupBlocked && (
                          <Box
                            style={{
                              backgroundColor: "#FFE4E1",
                              border: "1px solid #FF6B6B",
                              borderRadius: "8px",
                              padding: "16px",
                              margin: "20px 0",
                              textAlign: "left",
                            }}
                          >
                            <Typography
                              style={{
                                color: "#D32F2F",
                                fontSize: "14px",
                                fontWeight: "bold",
                                marginBottom: "8px",
                              }}
                            >
                              🚫 Popup Blocked
                            </Typography>
                            <Typography
                              style={{
                                color: "#D32F2F",
                                fontSize: "12px",
                                marginBottom: "12px",
                              }}
                            >
                              Your browser blocked the verification popup.
                              Please:
                            </Typography>
                            <Box style={{ marginLeft: "16px" }}>
                              <Typography
                                style={{
                                  color: "#D32F2F",
                                  fontSize: "12px",
                                  marginBottom: "4px",
                                }}
                              >
                                1. Click the popup blocker icon in your
                                browser's address bar
                              </Typography>
                              <Typography
                                style={{
                                  color: "#D32F2F",
                                  fontSize: "12px",
                                  marginBottom: "4px",
                                }}
                              >
                                2. Select "Always allow popups from this site"
                              </Typography>
                              <Typography
                                style={{
                                  color: "#D32F2F",
                                  fontSize: "12px",
                                  marginBottom: "12px",
                                }}
                              >
                                3. Click "Restart Verification" again
                              </Typography>
                            </Box>
                            <Button
                              variant="contained"
                              onClick={handleManualPopupOpen}
                              style={{
                                backgroundColor: "#D32F2F",
                                color: "white",
                                fontSize: "12px",
                                padding: "8px 16px",
                                marginRight: "8px",
                              }}
                            >
                              Try Again
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={() => setPopupBlocked(false)}
                              style={{
                                borderColor: "#D32F2F",
                                color: "#D32F2F",
                                fontSize: "12px",
                                padding: "8px 16px",
                              }}
                            >
                              Dismiss
                            </Button>
                          </Box>
                        )}

                        {/* Navigation Buttons */}
                        <Box
                          marginTop={isMobile ? "32px" : "60px"}
                          display="flex"
                          justifyContent="space-between"
                          width="100%"
                        >
                          <Button
                            variant="outlined"
                            className={creatorClasses.button}
                            onClick={() => prevStep()}
                            style={{
                              backgroundColor: "transparent",
                              border: "1px solid #FE5A97",
                              color: "#FE5A97",
                            }}
                          >
                            Back
                          </Button>

                          <Box className={creatorClasses.buttonContainer}>
                            <Button
                              variant="contained"
                              className={creatorClasses.button}
                              onClick={async () => {
                                if (verificationStatus === "Approved") {
                                  try {
                                    setIsFinalizing(true);
                                    const res: any = await editProfileDataAPI({
                                      isOnboarding: true,
                                      isOnboaring: true,
                                      validated: true,
                                      contentProviderAgreementAccepted: true,
                                      ...getCreatorOnboardingFinalizeFields(),
                                    });
                                    if (res?.user && user) {
                                      dispatch(
                                        setUser({
                                          ...user,
                                          ...res.user,
                                          ...(stagingCreatorBypass
                                            ? { ageProofed: true }
                                            : {}),
                                        })
                                      );
                                    }
                                    showToast("Onboarding completed!", "success");
                                    clearSavedStep();
                                    trackOnboardingCompleted({
                                      account_type: "model",
                                      user_id: user?.id,
                                    });
                                    history.push("/");
                                  } catch (e: any) {
                                    showToast(
                                      e?.message ||
                                        "Failed to complete onboarding",
                                      "error"
                                    );
                                  } finally {
                                    setIsFinalizing(false);
                                  }
                                } else {
                                  startDiditVerification();
                                }
                              }}
                              startIcon={<RefreshCw size={18} />}
                            >
                              {verificationStatus === "Approved"
                                ? "Complete"
                                : "Restart Verification"}
                            </Button>
                          </Box>
                        </Box>
                      </>
                    );
                  }

                  // If verification not started, show full content
                  return (
                    <>
                      {/* New Identity Verification Content */}
                      <Box
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          marginBottom: "32px",
                          width: "100%",
                        }}
                      >
                        {/* Main Image */}
                        <img
                          src={IdentityVerificationIcon}
                          alt="Identity Verification"
                          style={{
                            width: isMobile ? "120px" : "234px",
                            height: "auto",
                            marginBottom: "24px",
                            alignSelf: "center",
                          }}
                        />

                        {/* Title */}
                        <Typography
                          style={{
                            fontSize: isMobile ? "20px" : "32px",
                            fontWeight: isMobile ? 700 : 600,
                            color: "#FFFFFF",
                            textAlign: "center",
                            marginBottom: "16px",
                            width: "100%",
                          }}
                        >
                          Complete identity verification
                        </Typography>

                        {/* Subtitle */}
                        <Typography
                          style={{
                            fontSize: isMobile ? "12px" : "20px",
                            fontWeight: 400,
                            color: isMobile ? "#FFFFFF" : "#FFFFFFCC",
                            textAlign: "center",
                            marginBottom: "32px",
                            maxWidth: "600px",
                            width: "100%",
                          }}
                        >
                          We need to quickly verify your identity to keep the
                          platform safe for everyone.
                        </Typography>
                        {isMobile && (
                          <Typography
                            style={{
                              fontSize: "12px",
                              fontWeight: 400,
                              color: "#FFFFFF",
                              textAlign: "center",
                              marginBottom: "32px",
                              maxWidth: "600px",
                              width: "100%",
                            }}
                          >
                            Verification is handled through a secure service so
                            your data stays private and is used only for
                            identification purposes.
                          </Typography>
                        )}

                        {/* Navigation Buttons for Mobile - Show after subtitle */}
                        {isMobile && (
                          <Box
                            marginBottom="32px"
                            display="flex"
                            justifyContent="space-between"
                            width="100%"
                          >
                            <Button
                              variant="outlined"
                              className={creatorClasses.button}
                              onClick={() => prevStep()}
                              style={{
                                backgroundColor: "transparent",
                                border: "1px solid #FE5A97",
                                color: "#FE5A97",
                              }}
                            >
                              Back
                            </Button>

                            <Box className={creatorClasses.buttonContainer}>
                              <Button
                                variant="contained"
                                className={creatorClasses.button}
                                onClick={() => {
                                  // If verification is approved, go to next step
                                  if (verificationStatus === "Approved") {
                                    nextStep();
                                  } else {
                                    // Otherwise, start verification
                                    startDiditVerification();
                                  }
                                }}
                              >
                                {verificationStatus === "Approved"
                                  ? "Next"
                                  : "Start verification"}
                              </Button>
                            </Box>
                          </Box>
                        )}

                        {/* Steps Blocks */}
                        <Box
                          style={{
                            display: "flex",
                            flexDirection: isMobile ? "column" : "row",
                            gap: "16px",
                            width: "100%",
                            marginBottom: "32px",
                          }}
                        >
                          {/* Step 1 */}
                          <Box
                            style={{
                              backgroundColor: "#1F1F1F",
                              borderRadius: "12px",
                              padding: "24px",
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              textAlign: "left",
                            }}
                          >
                            <img
                              src={Step1Icon}
                              alt="Step 1"
                              style={{
                                width: "64px",
                                height: "64px",
                                marginBottom: "16px",
                              }}
                            />
                            <Typography
                              style={{
                                fontSize: "16px",
                                fontWeight: 600,
                                color: "#FFFFFF",
                                marginBottom: "8px",
                              }}
                            >
                              Step 1
                            </Typography>
                            <Typography
                              style={{
                                fontSize: "14px",
                                fontWeight: 400,
                                color: "#FFFFFF",
                                lineHeight: 1.5,
                              }}
                            >
                              Take a clear photo of your ID following the
                              on-screen instructions.
                            </Typography>
                          </Box>

                          {/* Step 2 */}
                          <Box
                            style={{
                              backgroundColor: "#1F1F1F",
                              borderRadius: "12px",
                              padding: "24px",
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              textAlign: "left",
                            }}
                          >
                            <img
                              src={Step2Icon}
                              alt="Step 2"
                              style={{
                                width: "64px",
                                height: "64px",
                                marginBottom: "16px",
                              }}
                            />
                            <Typography
                              style={{
                                fontSize: "16px",
                                fontWeight: 600,
                                color: "#FFFFFF",
                                marginBottom: "8px",
                              }}
                            >
                              Step 2
                            </Typography>
                            <Typography
                              style={{
                                fontSize: "14px",
                                fontWeight: 400,
                                color: "#FFFFFF",
                                lineHeight: 1.5,
                              }}
                            >
                              Take a quick selfie and complete the liveness
                              check.
                            </Typography>
                          </Box>

                          {/* Step 3 */}
                          <Box
                            style={{
                              backgroundColor: "#1F1F1F",
                              borderRadius: "12px",
                              padding: "24px",
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              textAlign: "left",
                            }}
                          >
                            <img
                              src={Step3Icon}
                              alt="Step 3"
                              style={{
                                width: "64px",
                                height: "64px",
                                marginBottom: "16px",
                              }}
                            />
                            <Typography
                              style={{
                                fontSize: "16px",
                                fontWeight: 600,
                                color: "#FFFFFF",
                                marginBottom: "8px",
                              }}
                            >
                              Step 3
                            </Typography>
                            <Typography
                              style={{
                                fontSize: "14px",
                                fontWeight: 400,
                                color: "#FFFFFF",
                                lineHeight: 1.5,
                              }}
                            >
                              We'll review your details and show your
                              verification result.
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* Dynamic Status Display - Only show if verification not started */}
                      {(() => {
                        const verificationStatus =
                          diditVerificationData?.didit_verifications?.[0]
                            ?.status || "Not Started";
                        const isVerificationStarted =
                          sessionUrl ||
                          verificationStatus === "In Progress" ||
                          verificationStatus === "Pending";

                        // Only show status messages if verification not started
                        if (!isVerificationStarted) {
                          const statusInfo =
                            getStatusMessage(verificationStatus);
                          return (
                            <Box
                              style={{ textAlign: "center", margin: "20px 0" }}
                            >
                              {statusInfo.title && (
                                <Typography
                                  style={{
                                    color: statusInfo.color,
                                    fontSize: "16px",
                                    marginBottom: "10px",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {statusInfo.title}
                                </Typography>
                              )}
                              {statusInfo.message && (
                                <Typography
                                  style={{
                                    color: "#FFFFFF",
                                    fontSize: "14px",
                                    marginBottom: "20px",
                                    lineHeight: "1.5",
                                  }}
                                >
                                  {statusInfo.message}
                                </Typography>
                              )}
                            </Box>
                          );
                        }
                        return null;
                      })()}

                      {/* Navigation Buttons - Desktop only (mobile buttons are above steps) */}
                      {!isMobile && (
                        <Box
                          marginTop="60px"
                          display="flex"
                          justifyContent="space-between"
                          width="100%"
                        >
                          <Button
                            variant="outlined"
                            className={creatorClasses.button}
                            onClick={() => prevStep()}
                            style={{
                              backgroundColor: "transparent",
                              border: "1px solid #FE5A97",
                              color: "#FE5A97",
                            }}
                          >
                            Back
                          </Button>

                          <Box className={creatorClasses.buttonContainer}>
                            <Button
                              variant="contained"
                              className={creatorClasses.button}
                              onClick={() => {
                                const verificationStatus =
                                  diditVerificationData
                                    ?.didit_verifications?.[0]?.status ||
                                  "Not Started";
                                // If verification is approved, go to next step
                                if (verificationStatus === "Approved") {
                                  nextStep();
                                } else {
                                  // Otherwise, start verification
                                  startDiditVerification();
                                }
                              }}
                            >
                              {(() => {
                                const verificationStatus =
                                  diditVerificationData
                                    ?.didit_verifications?.[0]?.status ||
                                  "Not Started";
                                return verificationStatus === "Approved"
                                  ? "Next"
                                  : "Start verification";
                              })()}
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </>
                  );
                })()}
              </Paper>
            </>
          )
        )}
      </Box>

      {/* Upload Media Modal */}
      <UploadMediaModal
        open={uploadMediaModalOpen}
        onClose={() => setUploadMediaModalOpen(false)}
        onSave={file => {
          // Handle file save based on current step and user type
          if (step === 2) {
            if (selectedType === "model") {
              handleCreatorFieldChange("profileImg", file);
            } else {
              handleViewerFieldChange("profileImg", file);
            }
          }
        }}
        acceptedTypes={["SVG", "PNG", "GIF"]}
        maxDimensions="800x400px"
      />
    </OnboardingLayout>
  );
};

export default ChooseUserType;
